import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from './supabaseClient';
import TKTooltip from './TKTooltip';
import { newBlockField } from './contractFields';
import ContractDocxImportModal from './ContractDocxImportModal';
import MergeFieldBank, { useTokenTarget } from './MergeFieldBank';
import RichTextBox from './RichTextBox';
import ContractOptions, { optionPrice, seedFirstChoice } from './ContractOptions';
import { money } from './billingModels';
import ContractPdf from './ContractPdf';
import {
  BLOCK_ORDER, BLOCK_TYPES, DEFAULT_LAYOUT, DEFAULT_STYLES, docColorCss, FILL_MODES, isHexColor, STYLE_NAMES,
  applyFieldValues, coverBlocks, isDeferredToken, newBlock, tableColumnWidths, tableGrid,
  blockStyleName, clauseNumberPrefix, COLUMN_CHILD_TYPES, columnsOf, computeClauseNumbers,
  blockPlainText, cloneBlockTree, flattenBlocks, HEADING_LEVELS, isRefToken, removeBlock as removeFromTree, replaceBlock,
  refTargetId, refToken, resolveDocumentToken, resolveLines, validateBlocks,
} from './contractBlocks';

// Writing a contract inside TimeKeep. Chunk 1.6, spec §19.
//
// Three panes: the sections in order, a live preview, and the properties of
// whichever section is selected. Deliberately NOT a free-placement canvas and
// not a word processor -- sections flow in order, and hierarchy comes from
// named styles. That constraint is what keeps the output consistent and the
// PDF renderer tractable.
//
// The preview here is an approximation drawn in HTML. The document that gets
// signed is rendered server-side by _shared/contractBlockRender.ts, from the
// same block model (src/contractBlocks.js), which is why those two are pinned
// against each other by scripts/test-contract-blocks.mjs. Line breaks and
// pagination will differ from the PDF; wording, order and styling will not.

/**
 * The lines a signature column can carry, in the order they stack.
 *
 * Austin's own layout is signature / full name / job title / date signed, and
 * that is the "Standard set". The rest are here because a signature block is
 * where documents differ most: some want initials, some want the signer's
 * email on the page, some want a typed line of their own.
 *
 * Name, title and email are MERGE fields scoped to the signer, not boxes the
 * signer fills: TimeKeep already knows who it sent the document to, and a
 * printed name that disagreed with the signer record would be worse than no
 * printed name. They are blank in the sent PDF and filled on the executed
 * copy, which is the moment they mean anything.
 */
export const SIGNATURE_LINES = {
  signature:   { kind: 'signature',   label: 'Signature', caption: (who) => who,
                 hint: 'A line they sign on.' },
  initials:    { kind: 'initials',    label: 'Initials', caption: () => 'Initials',
                 hint: 'A small box for initials.' },
  // ASKABLE lines can be filled in by TimeKeep from the signer's record, or
  // typed by the signer as they sign. Name and email TimeKeep genuinely knows
  // -- it emailed them. A JOB TITLE it usually does not, and a merge field
  // that resolves to nothing prints a blank line nobody was ever asked to
  // fill: which is exactly what happened on the first real signing.
  name:        { kind: 'merge', token: '{{signer_name}}', label: 'Full name', askable: true,
                 caption: () => 'Name', hint: 'Their name. Filled in from the signer by default.' },
  title:       { kind: 'merge', token: '{{signer_title}}', label: 'Job title', askable: true,
                 askByDefault: true,
                 caption: () => 'Title', hint: 'Their job title. Asked for at signing by default, because TimeKeep rarely knows it.' },
  email:       { kind: 'merge', token: '{{signer_email}}', label: 'Email', askable: true,
                 caption: () => 'Email', hint: 'Their email address. Filled in from the signer by default.' },
  date_signed: { kind: 'date_signed', label: 'Date signed', caption: () => 'Date',
                 hint: 'The date they actually signed.' },
  text:        { kind: 'text', label: 'Blank line', caption: () => 'Blank line',
                 hint: 'An empty line they can type into.' },
};

export const SIGNATURE_LINE_ORDER = ['signature', 'name', 'title', 'email', 'initials', 'date_signed', 'text'];

/** What "Standard set" fills in. */
const STANDARD_LINES = ['signature', 'name', 'title', 'date_signed'];

const PANEL = (C) => ({
  background: C?.surface, border: `1px solid ${C?.border}`, borderRadius: '10px',
  display: 'flex', flexDirection: 'column', minHeight: 0,
});

/**
 * Inline runs -> React, so the preview shows bold, italic and merge values.
 *
 * Resolution goes through resolveLines, the same function the PDF renderer
 * uses: typed line breaks break here too, and a line whose tokens have no
 * value disappears here exactly as it will on the page. A line quietly
 * vanishing would be baffling on its own, so what was missing is named
 * underneath rather than left to be discovered in the sent document.
 */
function InlineText({ text, resolve, deferred, C, prefix = null }) {
  const blanks = [];
  const lines = resolveLines(text, resolve, (token) => {
    if (!blanks.includes(token)) blanks.push(token);
  }, deferred);
  return (
    <>
      {lines.map((runs, li) => (
        <span key={li} style={{ display: 'block' }}>
          {/* A clause number belongs INSIDE its first line, the way the
              renderer draws it -- hung above it, the preview stops being a
              promise about the PDF. */}
          {li === 0 && prefix ? <span>{prefix}</span> : null}
          {runs.length === 0 ? (li === 0 && prefix ? null : '\u00a0') : runs.map((run, i) => (
            run.blank ? (
              // A gap held open for a value only known at signing. Drawn as a
              // blank here because that is what the PDF does.
              <span key={i} title={`${run.token} is filled in when the contract is signed`}
                    style={{ display: 'inline-block', minWidth: '120px',
                             borderBottom: `1px solid ${C?.muted}`, verticalAlign: 'baseline' }} />
            ) : (
              <span key={i} style={{ fontWeight: run.bold ? 700 : undefined, fontStyle: run.italic ? 'italic' : undefined }}>
                {run.text}
              </span>
            )
          ))}
        </span>
      ))}
      {blanks.length > 0 && (
        <span style={{ display: 'block', fontSize: '10px', color: C?.badgeAmber, marginTop: '3px' }}>
          {blanks.join(', ')} {blanks.length === 1 ? 'has' : 'have'} no value on this contract, so
          {blanks.length === 1 ? ' it is' : ' they are'} left out.
        </span>
      )}
    </>
  );
}

/**
 * Insert a reference to another clause.
 *
 * It stores the SECTION's id, never the number showing today -- that is the
 * whole point: a reference typed as "Section 6.1" is wrong the moment anything
 * is inserted above clause 6, and nothing tells you.
 */
function CrossRefPicker({ sections, onInsert, currentId, C, css }) {
  const [open, setOpen] = useState(false);
  const usable = sections.filter((s) => s.id !== currentId);
  if (!usable.length) return null;
  return (
    <div style={{ marginTop: '8px' }}>
      <button onClick={() => setOpen((v) => !v)}
              style={{ background: 'none', border: `1px solid ${C?.border}`, borderRadius: '6px',
                       color: C?.text, fontSize: '11px', padding: '4px 8px', cursor: 'pointer' }}>
        {open ? 'Hide cross-references' : 'Insert a cross-reference'}
      </button>
      {open && (
        <div style={{ marginTop: '6px', maxHeight: '150px', overflowY: 'auto',
                      border: `1px solid ${C?.border}`, borderRadius: '6px', padding: '4px' }}>
          {usable.map((sec) => (
            <button key={sec.id} onClick={() => onInsert(refToken(sec.id))}
                    title="Inserts a reference that follows this clause if it moves"
                    style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none',
                             border: 'none', color: C?.text, fontSize: '11px', padding: '4px 6px',
                             cursor: 'pointer', borderRadius: '4px' }}>
              <span style={{ color: C?.accent, fontWeight: 700 }}>{sec.number}</span>
              {'  '}
              <span style={{ color: C?.muted }}>
                {String(sec.text || '').replace(/\*\*/g, '').slice(0, 34) || '(untitled)'}
              </span>
            </button>
          ))}
          <div style={{ fontSize: '10px', color: C?.muted, padding: '4px 6px', lineHeight: 1.5 }}>
            The number updates itself if the document is reordered.
          </div>
        </div>
      )}
    </div>
  );
}

const miniBtn = (C) => ({
  background: 'none', border: `1px solid ${C?.border}`, borderRadius: '4px',
  color: C?.muted, fontSize: '10px', lineHeight: 1, padding: '4px 5px', cursor: 'pointer',
});

/** Plain names for the styles, since "h2" is not what anyone calls it. */
const STYLE_LABELS = {
  h1: 'Heading 1', h2: 'Heading 2', h3: 'Heading 3',
  body: 'Body text', caption: 'Caption', quote: 'Quote',
};

/**
 * Pick a color, or leave it at the document's default.
 *
 * A native swatch for choosing and a text box for typing an exact brand value,
 * because a brand color arrives as "#1E6FD9" in an email from a designer and
 * hunting for it in a picker is the wrong job. Clearing it returns the block or
 * style to the default rather than storing a color that happens to match --
 * so changing the default later still moves everything that never chose.
 */
function ColorField({ label, hint, value, fallback, onChange, C, css }) {
  const resolved = docColorCss(value || fallback);
  const [typed, setTyped] = useState(value && value.startsWith('#') ? value : '');
  useEffect(() => { setTyped(value && value.startsWith('#') ? value : ''); }, [value]);
  const commit = (v) => {
    const t = String(v || '').trim();
    if (!t) { onChange(null); return; }
    const withHash = t.startsWith('#') ? t : `#${t}`;
    if (isHexColor(withHash)) onChange(withHash.toUpperCase());
  };
  return (
    <div style={{ marginBottom: '10px' }}>
      {label && <div style={{ ...(css?.lbl || {}) }}>{hint ? <TKTooltip text={hint}>{label} ⓘ</TKTooltip> : label}</div>}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <input type="color" value={resolved} onChange={(e) => onChange(e.target.value.toUpperCase())}
               title="Pick a color"
               style={{ width: '30px', height: '26px', padding: 0, border: `1px solid ${C?.border}`,
                        borderRadius: '5px', background: 'transparent', cursor: 'pointer', flexShrink: 0 }} />
        <input value={typed} placeholder={value ? '' : 'Default'}
               onChange={(e) => setTyped(e.target.value)}
               onBlur={(e) => commit(e.target.value)}
               onKeyDown={(e) => { if (e.key === 'Enter') commit(e.currentTarget.value); }}
               style={{ ...(css?.inp || {}), flex: 1, minWidth: 0, fontFamily: 'ui-monospace, monospace' }} />
        {value && (
          <button onClick={() => onChange(null)} title="Back to the default color"
                  style={{ background: 'none', border: `1px solid ${C?.border}`, borderRadius: '5px',
                           color: C?.muted, fontSize: '10px', padding: '4px 7px', cursor: 'pointer' }}>
            Reset
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * The thin strip between two sections, where a new one can go in.
 *
 * It keeps its own hover state ON PURPOSE. Lifting it to the editor would make
 * every mouse move across the document re-render all 75 blocks, each of which
 * re-parses its inline markup — the preview would crawl. A gap re-renders only
 * itself.
 */
/** type -> the words shown in a picker. Read from the registry so a new block
 *  type appears in every picker without being listed again here. */
const BLOCK_LABELS = Object.fromEntries(
  Object.entries(BLOCK_TYPES).map(([k, v]) => [k, v.label]));

function InsertGap({ onPick, types, labels, css, C }) {
  const [hover, setHover] = useState(false);
  const [open, setOpen] = useState(false);
  return (
    <div onMouseEnter={() => setHover(true)}
         onMouseLeave={() => { setHover(false); setOpen(false); }}
         style={{ position: 'relative', height: open ? 'auto' : '12px', margin: '0 -12px' }}>
      {!open && (
        <button type="button" onClick={() => setOpen(true)} title="Add a section here"
                style={{ position: 'absolute', inset: 0, width: '100%', cursor: 'pointer',
                         display: 'flex', alignItems: 'center', gap: '6px', padding: '0 12px',
                         background: 'transparent', border: 'none',
                         opacity: hover ? 1 : 0, transition: 'opacity 0.12s' }}>
          <span style={{ fontSize: '11px', color: C?.accent, lineHeight: 1 }}>+</span>
          <span style={{ flex: 1, height: '1px', background: C?.accent }} />
        </button>
      )}
      {open && (
        <div style={{ margin: '6px 12px', padding: '8px', borderRadius: '6px',
                      border: `1px solid ${C?.accent}`, background: C?.surface,
                      display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
          {types.map((t) => (
            <button key={t} onClick={() => { setOpen(false); onPick(t); }}
                    style={{ ...(css?.smBtn?.() || {}), fontSize: '10px' }}>{labels[t]}</button>
          ))}
          <button onClick={() => setOpen(false)}
                  style={{ ...(css?.btn?.('ghost') || {}), fontSize: '10px' }}>Cancel</button>
        </div>
      )}
    </div>
  );
}

export default function ContractDocumentEditor({
  contract, roles = [], signers = [], clients = [], users = [], org, css, C, onClose, onSaved, onError,
  // Template mode: the same editor, editing wording that isn't attached to a
  // contract yet. Signature and date boxes are deliberately NOT placeable here
  // -- a contract_fields row points at a role on a real envelope, and a
  // template has no people on it. They're placed per contract after creation.
  templateMode = false, templateName = '', onSaveBlocks,
}) {
  const [blocks, setBlocks] = useState(() => (Array.isArray(contract.blocks) ? contract.blocks : []));
  const [fields, setFields] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  // Three panels is a lot of screen at once. Collapsing the list leaves the
  // document and its settings, which is what you are actually reading.
  const [sectionsOpen, setSectionsOpen] = useState(true);
  // One row element per block, so selecting in the document can bring the
  // matching row into view. A ref map rather than querySelector: the rows are
  // React's to own, and an id-based lookup would break the moment a block id
  // contained anything a selector treats specially.
  const rowRefs = useRef({});
  // The same, for the document pane. Finding a section in the list is only
  // half of finding it -- without this the document stays wherever it was,
  // which on a 76-section contract is nowhere near the match.
  const previewRefs = useRef({});
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  // Pricing options are BUILT HERE, beside the section that prints them.
  // They used to be built on the contract's detail panel while the section
  // lived in this editor -- Austin: "It just does not work as is needing to do
  // it in 2 separate places." The rows still belong to the contract rather
  // than to the document, so they are loaded and saved directly rather than
  // riding along with the blocks.
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [optionGroups, setOptionGroups] = useState([]);
  const [optionRows, setOptionRows] = useState([]);
  const [adding, setAdding] = useState(false);
  const dragFrom = useRef(null);
  const [dragOver, setDragOver] = useState(null);

  const selected = flattenBlocks(blocks).find((b) => b.id === selectedId) || null;
  // The side-by-side a selected section lives in, if it is in one. Selecting a
  // section inside a column replaced the whole panel with that section's
  // settings and gave no way back to the container -- so adding a second
  // section to the column meant finding the container again in the list.
  // useCallback, because the scroll-to-selection effect depends on it: a new
  // function identity every render would re-run that effect every render, and
  // the document pane would scroll back to the selection while you were
  // reading somewhere else.
  const parentOf = useCallback((id) => blocks.find(
    (b) => b.type === 'columns' && columnsOf(b).some((col) => col.some((c) => c.id === id))) || null,
  [blocks]);
  const selectedParent = selectedId ? parentOf(selectedId) : null;

  // Follow the selection in the list. 'nearest' deliberately: it scrolls only
  // when the row is actually out of view, so clicking a row that is already
  // visible does not make the list jump under the cursor.
  useEffect(() => {
    if (!selectedId) return;
    if (sectionsOpen) rowRefs.current[selectedId]?.scrollIntoView({ block: 'nearest' });
    // 'nearest' so selecting something already on screen doesn't jolt the page;
    // a block inside a column has no wrapper of its own, so fall back to its
    // parent, which is the thing the document pane actually lays out.
    const own = previewRefs.current[selectedId];
    const parent = own ? null : parentOf(selectedId);
    (own || (parent && previewRefs.current[parent.id]))?.scrollIntoView({ block: 'nearest' });
  }, [selectedId, sectionsOpen, parentOf]);
  // Styles are edited here (colors), so they are state rather than derived.
  const [styles, setStyles] = useState(() => ({
    ...DEFAULT_STYLES, ...(contract.doc_styles || org?.contract_doc_styles || {}),
  }));
  const setStyleColor = (name, color) => {
    setStyles((st) => ({ ...st, [name]: { ...(st[name] || {}), color: color || DEFAULT_STYLES[name]?.color || 'ink' } }));
    setDirty(true);
  };
  // Page setup is edited here, so it is state rather than a derived constant.
  const [layout, setLayout] = useState(() => ({
    ...DEFAULT_LAYOUT,
    ...(contract.doc_layout || org?.contract_doc_layout || {}),
    logo: { ...DEFAULT_LAYOUT.logo, ...((contract.doc_layout || org?.contract_doc_layout || {}).logo || {}) },
  }));
  const clauseNumbers = useMemo(() => computeClauseNumbers(blocks, layout), [blocks, layout]);

  // ── Finding a section ───────────────────────────────────────────────
  //
  // Austin, on a 76-section MSA: scrolling the list to find one clause does
  // not work. The browser's own find is no help either -- it searches the
  // rendered preview, not the list, and a section scrolled out of the list is
  // not in the DOM to match.
  //
  // What is searched is deliberately everything a writer would search BY: the
  // words, a prompted section's label and guidance, table cells and list
  // items (blockPlainText already walks those), and the CLAUSE NUMBER, because
  // on a numbered contract "6.1" is how people refer to a clause -- and that
  // number exists nowhere in the stored text, being derived from position.
  const [query, setQuery] = useState('');

  const searchTextFor = useCallback((b) => [
    blockPlainText(b),
    b.label || '', b.hint || '', b.default_text || '',
    clauseNumbers[b.id] || '',
    BLOCK_TYPES[b.type]?.label || '',
  ].filter(Boolean).join(' ').toLowerCase(), [clauseNumbers]);

  /**
   * Top-level rows that match, and for each the block to select.
   *
   * A match INSIDE a column surfaces the column's row -- that is what the list
   * shows -- but selecting it jumps to the child that actually matched, which
   * is the thing being looked for.
   */
  const search = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    const hits = new Map();
    blocks.forEach((b) => {
      if (searchTextFor(b).includes(q)) { hits.set(b.id, b.id); return; }
      const child = flattenBlocks([b]).find((c) => c.id !== b.id && searchTextFor(c).includes(q));
      if (child) hits.set(b.id, child.id);
    });
    return hits;
  }, [query, blocks, searchTextFor]);

  // Reordering a filtered list is ambiguous -- dropping onto a row with hidden
  // rows between means moving it somewhere nobody saw. So while a search is
  // running, rows don't drag and the move arrows are hidden.
  const filtering = !!search;
  // Every section that carries a number, for the cross-reference picker.
  const numberedSections = useMemo(
    () => blocks.filter((b) => clauseNumbers[b.id]).map((b) => ({ ...b, number: clauseNumbers[b.id] })),
    [blocks, clauseNumbers],
  );
  const [pageSetup, setPageSetup] = useState(false);
  const [textStyles, setTextStyles] = useState(false);
  const [importing, setImporting] = useState(false);
  // Which text field the cursor is in, so a merge-field chip knows where to go.
  const tokens = useTokenTarget();
  // Carried to the save so the wording can always be traced to the .docx it
  // came from -- kept on the contract, or on the template version.
  const [imported, setImported] = useState(null);
  const [headerUrl, setHeaderUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const hasCover = blocks.some((b) => b.type === 'page_break');
  /** Drops a cover in at the top and turns the cover rule on in one go. */
  const insertCover = () => {
    setBlocks((list) => [...coverBlocks(), ...list]);
    setLayout((l) => ({ ...l, cover_page: true }));
    setSelectedId(null);
    setDirty(true);
  };

  const setLogo = (patch) => { setLayout((l) => ({ ...l, logo: { ...l.logo, ...patch } })); setDirty(true); };

  const loadFields = useCallback(async () => {
    if (templateMode) { setFields([]); return; }
    const { data, error } = await supabase.from('contract_fields')
      .select('*').eq('contract_id', contract.id).eq('anchor_kind', 'block')
      .order('block_offset');
    if (error) { onError?.('Loading the document’s fields failed: ' + error.message); return; }
    setFields(data || []);
  }, [contract.id, templateMode, onError]);
  useEffect(() => { loadFields(); }, [loadFields]);

  /**
   * The pricing choices on offer, for the preview and the builder.
   *
   * A TEMPLATE has no contract row, so it has none: contract.id is a template
   * id here, and asking for its options would query a foreign key that cannot
   * match. The Pricing options section still renders in template wording, and
   * says what it will do once a contract is made from it.
   */
  const loadOptions = useCallback(async () => {
    if (templateMode) { setOptionGroups([]); setOptionRows([]); return; }
    const [g, o] = await Promise.all([
      supabase.from('contract_option_groups')
        .select('id, label, help_text, selection, is_required, sort_order')
        .eq('contract_id', contract.id).order('sort_order'),
      supabase.from('contract_options')
        .select('id, group_id, label, body_text, billing_model_id, billing_config, charges, is_recommended, sort_order')
        .eq('contract_id', contract.id).order('sort_order'),
    ]);
    if (g.error || o.error) {
      onError?.('Loading pricing options failed: ' + (g.error || o.error).message);
      return;
    }
    setOptionGroups(g.data || []);
    setOptionRows(o.data || []);
  }, [contract.id, templateMode, onError]);
  useEffect(() => { loadOptions(); }, [loadOptions]);

  /**
   * Open the builder, creating the first choice when there is none.
   *
   * Adding the section and finding an empty box is the thing that made this
   * confusing; adding the section and landing in front of two options to name
   * is not. Seeding only happens on a DRAFT with nothing on offer yet, so it
   * can never add to a contract that already has choices.
   */
  const openOptions = async (seed = false) => {
    if (templateMode) return;
    if (seed && contract.workflow_status === 'draft' && optionGroups.length === 0) {
      setBusy(true);
      const r = await seedFirstChoice(contract);
      setBusy(false);
      if (r.error) { onError?.('Setting up the first choice failed: ' + r.error); return; }
      await loadOptions();
    }
    setOptionsOpen(true);
  };

  // What the counterparty will actually read: merge values from this contract.
  // A template is written before anyone is named, so the preview stands in an
  // obviously fake counterparty rather than leaving the token raw.
  const counterpartyLabel = templateMode ? 'Acme Corp' : contract.counterparty_name
    || (contract.client_id
      ? (() => { const c = clients.find((x) => x.id === contract.client_id); return c?.billing_name || c?.name || null; })()
      : null)
    || (contract.member_user_id ? users.find((u) => u.id === contract.member_user_id)?.name || null : null);

  // The counterparty's own record, for the address tokens. A prospect who is
  // not in the system yet has no record, and a member has no address anywhere
  // in the schema -- in both cases the address tokens resolve to nothing and
  // the line carrying them is dropped, here and in the PDF alike.
  const counterparty = templateMode
    ? { address_line1: '1 Cherry Tree Ln', address_line2: 'Suite 200',
        city: 'Naperville', state: 'FL', zip: '02323' }
    : contract.client_id ? clients.find((x) => x.id === contract.client_id) || null
    // A prospect is not in the system, so what was typed about them at
    // creation IS their record.
    : contract.counterparty_details || null;

  // The same rule the renderer uses, so the preview and the PDF agree -- most
  // visibly for {{effective_date}} on a contract that takes effect on signature.
  const resolve = useCallback(
    (token) => {
      // A cross-reference shows the number its target currently carries, so the
      // preview says what the PDF will say. "[?]" means the target is gone or
      // no longer numbered -- validateBlocks refuses to send on that.
      if (isRefToken(token)) return clauseNumbers[refTargetId(token)] ?? '[?]';
      return resolveDocumentToken(token, { contract, org, signer: null, counterpartyLabel, counterparty });
    },
    [contract, org, counterpartyLabel, counterparty, clauseNumbers]);

  // The same rule the renderer applies: a value only known at signing is a
  // gap, not prose.
  const deferred = useCallback(
    (token) => isDeferredToken(token, { contract }),
    [contract]);

  const rendered = useMemo(() => applyFieldValues(blocks, contract.field_values || {}), [blocks, contract.field_values]);
  const validation = useMemo(() => validateBlocks(blocks, { layout }), [blocks, layout]);

  // Sections can now live inside a column, so every mutation goes through the
  // tree helpers rather than mapping the top-level list.
  const update = (id, patch) => {
    setBlocks((list) => replaceBlock(list, id, patch));
    setDirty(true);
  };
  const addBlock = (type) => {
    const b = newBlock(type);
    setBlocks((list) => {
      const at = selectedId ? list.findIndex((x) => x.id === selectedId) + 1 : list.length;
      return [...list.slice(0, at), b, ...list.slice(at)];
    });
    setSelectedId(b.id);
    setAdding(false);
    setDirty(true);
    afterAdd(type);
  };
  /** Add a section AT A POINT rather than after the selection. In a long
   *  contract you are almost never appending to the end -- you are inserting
   *  at clause 14 -- and dragging a new block up 60 rows is not a way to do
   *  that. Top-level only: the preview renders a side-by-side as one unit, and
   *  that panel has its own per-column controls. */
  const insertAt = (index, type) => {
    const b = newBlock(type);
    setBlocks((list) => [...list.slice(0, index), b, ...list.slice(index)]);
    setSelectedId(b.id);
    setDirty(true);
    afterAdd(type);
  };

  /**
   * A Pricing options section with nothing on offer is an empty box, and an
   * empty box was the whole problem. Adding one opens the builder with a first
   * choice ready to name; adding one to a contract that already has choices
   * just shows them.
   */
  const afterAdd = (type) => {
    if (type === 'options' && !templateMode) openOptions(true);
  };

  const removeBlock = async (id) => {
    const attached = fields.filter((f) => f.block_id === id);
    if (attached.length && !window.confirm(
      `This section has ${attached.length} signature or date field${attached.length === 1 ? '' : 's'} on it. Deleting it removes ${attached.length === 1 ? 'that field' : 'those fields'} too.`)) return;
    if (attached.length) {
      const { error } = await supabase.from('contract_fields').delete().in('id', attached.map((f) => f.id));
      if (error) { onError?.(error.message); return; }
      await loadFields();
    }
    setBlocks((list) => removeFromTree(list, id));
    if (selectedId === id) setSelectedId(null);
    setDirty(true);
  };
  /**
   * Copy a section, placed right after the original.
   *
   * `cloneBlockTree` re-ids EVERY block in the copy, not just the outer one --
   * see its comment for why a shared id makes editing the copy edit the
   * original. Fields are not copied: one signature cannot be in two places.
   */
  const duplicateBlock = (i) => {
    const src = blocks[i];
    if (!src) return;
    const copy = cloneBlockTree(src);
    setBlocks((list) => [...list.slice(0, i + 1), copy, ...list.slice(i + 1)]);
    setSelectedId(copy.id);
    setDirty(true);
  };

  const move = (from, to) => {
    if (to < 0 || to >= blocks.length || from === to) return;
    setBlocks((list) => {
      const next = [...list];
      const [b] = next.splice(from, 1);
      next.splice(to, 0, b);
      return next;
    });
    setDirty(true);
  };

  // ── Fields on a section ───────────────────────────────────────────
  // A role is a person on this envelope; contract_signers is the per-document
  // evidence row for that same person, which the edge functions still resolve
  // fields by, so both ids are written.
  const signerForRole = (role) => signers.find((s) => s.envelope_signer_id === role.envelope_signer_id) || null;

  /**
   * Does this field BE this line?
   *
   * An askable line can be a merge field or a text box the signer fills, and
   * switching between them must not look like deleting one line and adding
   * another. Their captions are fixed ('Name', 'Title', 'Email'), so the
   * caption is the stable identity; kind is not.
   */
  const matchesLine = (f, def) => {
    if (def.askable) {
      return (f.field_kind === 'merge' || f.field_kind === 'text') && f.label === def.caption();
    }
    return f.field_kind === def.kind && (def.kind !== 'merge' || f.merge_token === def.token);
  };

  const fieldForLine = (block, role, line) =>
    fields.find((f) => f.block_id === block.id && f.role_id === role.id
      && matchesLine(f, SIGNATURE_LINES[line])) || null;

  /** Which line types a person already has in this block. */
  const lineExists = (block, role, line) => !!fieldForLine(block, role, line);

  /** Switch an askable line between "TimeKeep fills it" and "they type it". */
  const setLineAsked = async (line, field, asked, role) => {
    const def = SIGNATURE_LINES[line];
    // A SIGNER-FILLED FIELD IS OFFERED TO EXACTLY ONE PERSON, matched by
    // signer_id -- by the signing page when it decides which boxes are yours,
    // and by submit-signature when it decides which it will accept a value
    // for. A box with no signer is shown to nobody and blocks nobody, so a
    // required one would not even be enforced: it would just print blank. That
    // is precisely the failure this whole control exists to remove, so it is
    // refused here rather than stored.
    const signerId = field.signer_id || signerForRole(role)?.id || null;
    if (asked && !signerId) {
      onError?.('Nobody is signing in that spot yet, so there is no one to ask. Add them under Edit details first.');
      return;
    }
    const { error } = await supabase.from('contract_fields').update({
      field_kind: asked ? 'text' : 'merge',
      merge_token: asked ? null : def.token,
      value_source: asked ? 'signer' : 'merge_token',
      signer_id: signerId,
      // Required is only meaningful for something a person fills in. A merge
      // field TimeKeep resolves itself cannot be "missing" at signing time.
      is_required: asked ? !!field.is_required : false,
    }).eq('id', field.id);
    if (error) { onError?.(error.message); return; }
    await loadFields();
  };

  const setLineRequired = async (field, required) => {
    const { error } = await supabase.from('contract_fields')
      .update({ is_required: required }).eq('id', field.id);
    if (error) { onError?.(error.message); return; }
    await loadFields();
  };

  /**
   * Keep a block's fields grouped by person and ordered by line type.
   *
   * The renderer reads a run of consecutive fields sharing a role as one
   * column, so the grouping IS the layout -- click order must not decide it.
   * Re-numbering after every change makes that invariant hold no matter how
   * someone builds the block up.
   */
  /**
   * Which person is in which column, derived from the fields themselves: the
   * order their roles first appear in block_offset order.
   *
   * The ORDER IS THE DATA -- there is nowhere else it needs to live, and a
   * role id stored in the block JSON would be meaningless in a template.
   * A role with no lines yet is appended, org side first: the heading above
   * the left column is conventionally "For <your organization>", so putting
   * yourself there by default is right more often than signing order is.
   */
  const signerOrderFor = (blockId, rows) => {
    const seen = [];
    rows.filter((f) => f.block_id === blockId)
      .sort((a, b) => (a.block_offset || 0) - (b.block_offset || 0))
      .forEach((f) => { if (f.role_id && !seen.includes(f.role_id)) seen.push(f.role_id); });
    [...roles].sort((a, b) => Number(!!b.is_org_side) - Number(!!a.is_org_side))
      .forEach((r) => { if (!seen.includes(r.id)) seen.push(r.id); });
    return seen;
  };

  const resequence = async (blockId, rows, orderOverride) => {
    const inBlock = rows.filter((f) => f.block_id === blockId);
    const roleOrder = orderOverride || signerOrderFor(blockId, rows);
    const rank = (f) => {
      const li = SIGNATURE_LINE_ORDER.findIndex((line) => {
        return matchesLine(f, SIGNATURE_LINES[line]);
      });
      return li === -1 ? SIGNATURE_LINE_ORDER.length : li;
    };
    const sorted = [...inBlock].sort((a, b) => {
      const ra = roleOrder.indexOf(a.role_id);
      const rb = roleOrder.indexOf(b.role_id);
      if (ra !== rb) return (ra === -1 ? 99 : ra) - (rb === -1 ? 99 : rb);
      return rank(a) - rank(b);
    });
    for (let i = 0; i < sorted.length; i++) {
      if (sorted[i].block_offset === i) continue;
      const { error } = await supabase.from('contract_fields')
        .update({ block_offset: i, sort_order: i }).eq('id', sorted[i].id);
      if (error) { onError?.(error.message); return; }
    }
  };

  const toggleLine = async (block, role, line) => {
    const def = SIGNATURE_LINES[line];
    const existing = fields.find((f) => f.block_id === block.id && f.role_id === role.id
      && matchesLine(f, def));
    if (existing) {
      const { error } = await supabase.from('contract_fields').delete().eq('id', existing.id);
      if (error) { onError?.(error.message); return; }
    } else {
      const who = role.person_name || role.name;
      // An askable line that defaults to being asked starts as a text box the
      // signer types into, not a merge field that may resolve to nothing --
      // but only when somebody is actually bound to the role to be asked. With
      // nobody there it falls back to the merge field, which at least fills
      // itself in if a signer is added later.
      const asked = !!(def.askable && def.askByDefault) && !!signerForRole(role);
      const { error } = await supabase.from('contract_fields').insert(newBlockField({
        kind: asked ? 'text' : def.kind,
        contractId: contract.id,
        orgId: contract.org_id,
        blockId: block.id,
        blockOffset: fields.filter((f) => f.block_id === block.id).length,
        roleId: role.id,
        signerId: signerForRole(role)?.id ?? null,
        mergeToken: asked ? null : def.token,
        label: def.caption(who),
        // A name or date filled in for them is not something to block a send on.
        isRequired: def.kind === 'signature',
      }));
      if (error) { onError?.(error.message); return; }
    }
    const { data } = await supabase.from('contract_fields')
      .select('*').eq('contract_id', contract.id).eq('anchor_kind', 'block');
    await resequence(block.id, data || []);
    await loadFields();
    setDirty(true);
  };

  /**
   * Move a person one place along -- left/right across columns, or up/down in
   * a single-column block. Their whole stack of lines travels with them.
   */
  const moveSigner = async (block, roleId, dir) => {
    const { data } = await supabase.from('contract_fields')
      .select('*').eq('contract_id', contract.id).eq('anchor_kind', 'block');
    const rows = data || [];
    const order = signerOrderFor(block.id, rows);
    const i = order.indexOf(roleId);
    const j = i + dir;
    if (i === -1 || j < 0 || j >= order.length) return;
    [order[i], order[j]] = [order[j], order[i]];
    await resequence(block.id, rows, order);
    await loadFields();
    setDirty(true);
  };

  // The signers of the selected signature block, in the order they appear in
  // its columns rather than in signing order -- so the list reads the way the
  // page will.
  const orderedRoles = (() => {
    if (!selected || selected.type !== 'signature_block') return roles;
    const order = signerOrderFor(selected.id, fields);
    return [...roles].sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id));
  })();

  /** Austin's layout in one click: signature, name, title, date. */
  const addStandardLines = async (block, role) => {
    for (const line of STANDARD_LINES) {
      if (!lineExists(block, role, line)) await toggleLine(block, role, line);
    }
  };

  /** A value TimeKeep fills in itself — nobody signs it, so it carries no role. */
  const toggleMergeField = async (block, token, label) => {
    const existing = fields.find((f) => f.block_id === block.id && f.merge_token === token);
    if (existing) {
      const { error } = await supabase.from('contract_fields').delete().eq('id', existing.id);
      if (error) { onError?.(error.message); return; }
    } else {
      const { error } = await supabase.from('contract_fields').insert(newBlockField({
        kind: 'merge',
        contractId: contract.id,
        orgId: contract.org_id,
        blockId: block.id,
        blockOffset: fields.filter((f) => f.block_id === block.id).length,
        mergeToken: token,
        label,
        // Filled at execution, so an empty box before then is expected, not a
        // document that failed to render.
        isRequired: false,
      }));
      if (error) { onError?.(error.message); return; }
    }
    await loadFields();
    setDirty(true);
  };

  // A letterhead lives in the same private bucket as the documents. The path
  // carries no contract segment, so only an admin of the org can read it --
  // the renderer reads it service-side when it draws the page.
  const uploadHeader = async (file) => {
    if (!file) return;
    if (!/^image\/(png|jpeg)$/.test(file.type)) {
      onError?.('A letterhead has to be a PNG or JPEG image.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      onError?.('That image is over 2MB. Letterheads are drawn small — a smaller file renders identically.');
      return;
    }
    setUploading(true);
    const ext = file.type === 'image/png' ? 'png' : 'jpg';
    const path = `${contract.org_id}/letterhead/${crypto.randomUUID()}.${ext}`;
    const { error } = await supabase.storage.from('contract-documents')
      .upload(path, file, { contentType: file.type, upsert: false });
    setUploading(false);
    if (error) { onError?.('Uploading the letterhead failed: ' + error.message); return; }
    setLogo({ source: 'upload', path, show: true, width_pct: 1 });
  };

  // Shown in the preview pane. Signed rather than public: the bucket is private.
  useEffect(() => {
    let canceled = false;
    (async () => {
      const lg = layout.logo || {};
      if (lg.show === false || lg.source === 'none') { setHeaderUrl(null); return; }
      if (lg.source === 'upload') {
        if (!lg.path) { setHeaderUrl(null); return; }
        const { data } = await supabase.storage.from('contract-documents').createSignedUrl(lg.path, 600);
        if (!canceled) setHeaderUrl(data?.signedUrl || null);
        return;
      }
      if (!canceled) setHeaderUrl(org?.invoice_logo_url || null);
    })();
    return () => { canceled = true; };
  }, [layout.logo, org?.invoice_logo_url]);

  const save = async () => {
    setBusy(true);
    if (templateMode) {
      const ok = await onSaveBlocks?.(blocks, styles, layout, imported);
      setBusy(false);
      if (!ok) return false;
      setDirty(false);
      return true;
    }
    const { error } = await supabase.from('contracts').update({
      blocks,
      doc_styles: styles,
      doc_layout: layout,
      // Only set when this save carries an import, so re-saving later never
      // claims wording came from a file it no longer resembles.
      ...(imported ? {
        imported_file_path: imported.provenance?.path ?? null,
        imported_sha256: imported.provenance?.sha256 ?? null,
        import_report: imported.report ?? null,
      } : {}),
    }).eq('id', contract.id);
    setBusy(false);
    if (error) { onError?.('Saving the document failed: ' + error.message); return false; }
    setDirty(false);
    setImported(null);
    await onSaved?.();
    return true;
  };

  // ── The real pages ────────────────────────────────────────────────
  //
  // The writing preview is a continuous HTML flow: it has no pages, so it can
  // never show a cover, a page-1 header, a table splitting, or a heading pushed
  // overleaf. Those are exactly the things people got wrong. This renders the
  // ACTUAL PDF through the same endpoint a send uses, so what you check is what
  // a counterparty receives -- and it renders the document ON SCREEN, unsaved
  // edits included, rather than the last saved version.
  const [mode, setMode] = useState('writing');          // 'writing' | 'pages'
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const [pdfWarnings, setPdfWarnings] = useState([]);
  const [pdfStamp, setPdfStamp] = useState(0);          // bumped to force a re-render
  const [pdfStale, setPdfStale] = useState(false);
  const pdfUrlRef = useRef(null);

  // One blob URL at a time. Without revoking, every keystroke-triggered render
  // leaks a whole PDF into the tab for as long as it stays open.
  const putPdfUrl = useCallback((url) => {
    if (pdfUrlRef.current) URL.revokeObjectURL(pdfUrlRef.current);
    pdfUrlRef.current = url;
    setPdfUrl(url);
  }, []);
  useEffect(() => () => { if (pdfUrlRef.current) URL.revokeObjectURL(pdfUrlRef.current); }, []);

  // Re-render when the document settles, not on every keystroke: each run is a
  // real PDF render on the server.
  useEffect(() => {
    if (mode !== 'pages') return undefined;
    if (templateMode) return undefined;          // a template has no contract to render against
    let canceled = false;
    const timer = setTimeout(async () => {
      setPdfBusy(true);
      setPdfError(null);
      try {
        const { data, error } = await supabase.functions.invoke('preview-contract-document', {
          body: {
            contract_id: contract.id,
            document: { blocks, doc_styles: styles, doc_layout: layout, field_values: contract.field_values || {} },
          },
        });
        if (canceled) return;
        // A 400 from this endpoint is the SAME gate that blocks sending -- a
        // dangling cross-reference, a required section left empty. Saying so
        // here is the whole point: it is found while writing, not at send.
        const msg = error
          ? (await error.context?.json?.().then((j) => j?.error).catch(() => null)) || error.message
          : (!data?.ok ? data?.error : null);
        if (msg) { setPdfError(msg); setPdfBusy(false); return; }
        const bytes = Uint8Array.from(atob(data.pdf_base64), (c) => c.charCodeAt(0));
        putPdfUrl(URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' })));
        setPdfWarnings(data.warnings || []);
        // We asked it to render what is on screen. If it rendered the stored
        // document instead, the server is older than this app -- say so rather
        // than letting the pages look authoritative.
        setPdfStale(data.rendered_from !== 'draft');
      } catch (e) {
        if (!canceled) setPdfError(e?.message || 'The pages could not be rendered.');
      }
      if (!canceled) setPdfBusy(false);
    }, 900);
    return () => { canceled = true; clearTimeout(timer); };
  }, [mode, blocks, styles, layout, contract.id, contract.field_values, templateMode, putPdfUrl, pdfStamp]);

  const closeEditor = async () => {
    if (dirty && !window.confirm('Close without saving your changes?')) return;
    onClose();
  };

  const lbl = { ...(css?.lbl || {}) };
  const smallBtn = (active) => ({
    padding: '4px 9px', borderRadius: '6px', fontSize: '11px', fontWeight: 600, cursor: 'pointer',
    border: `1px solid ${active ? C?.accent : C?.border}`,
    background: active ? `${C?.accent}1F` : 'transparent',
    color: active ? C?.accent : C?.text,
  });

  // ── Preview ───────────────────────────────────────────────────────
  const previewBlock = (b) => {
    const style = styles[b.style] || styles.body;
    const fieldsHere = fields.filter((f) => f.block_id === b.id);
    const common = {
      fontSize: `${(style.size || 10.5) * 1.25}px`,
      fontWeight: style.bold ? 700 : 400,
      fontStyle: style.italic ? 'italic' : 'normal',
      lineHeight: style.line || 1.4,
      // A block's own color wins over its style's. Named tones still follow
      // the app theme, so an uncolored document reads correctly in dark mode;
      // a chosen hex is the customer's brand and is shown exactly.
      color: (b.color || style.color) && (b.color || style.color) !== 'ink'
        ? ((b.color || style.color) === 'muted' ? C?.muted : docColorCss(b.color || style.color))
        : C?.textBright,
      marginTop: `${(style.space_before || 0) * 0.9}px`,
      marginBottom: `${(style.space_after || 0) * 0.9}px`,
      paddingLeft: style.indent ? `${style.indent}px` : undefined,
    };

    const fieldRow = fieldsHere.length > 0 && (
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '10px' }}>
        {fieldsHere.map((f) => (
          <div key={f.id} style={{ minWidth: f.field_kind === 'signature' ? '180px' : '120px' }}>
            <div style={{ height: f.field_kind === 'signature' ? '34px' : '18px',
                          borderBottom: `1px solid ${C?.border}` }} />
            <div style={{ fontSize: '9px', color: C?.muted, marginTop: '3px' }}>{f.label}</div>
          </div>
        ))}
      </div>
    );

    if (b.type === 'page_break') {
      return (
        <div style={{ margin: '18px 0', borderTop: `1px dashed ${C?.border}`, textAlign: 'center' }}>
          <span style={{ fontSize: '9px', color: C?.muted, background: C?.bg, padding: '0 8px', position: 'relative', top: '-7px' }}>
            page break
          </span>
        </div>
      );
    }
    if (b.type === 'options') {
      // The options as they will PRINT, not a note telling you to go and find
      // them somewhere else. The button is right here because this is where
      // you are when you decide they are wrong.
      const note = { fontSize: '11px', color: C?.muted, lineHeight: 1.6, marginTop: '6px',
                     border: `1px dashed ${C?.border}`, borderRadius: '6px', padding: '10px' };
      return (
        <div style={{ marginTop: common.marginTop, marginBottom: common.marginBottom }}>
          {b.text && <div style={{ ...common, fontWeight: 700,
                                   fontSize: `${(styles.h2?.size || 14) * 1.25}px` }}>{b.text}</div>}

          {templateMode ? (
            <div style={note}>
              The pricing choices live on a contract, not on a template — this section will
              print whatever is on offer on each contract made from this wording.
            </div>
          ) : optionGroups.length === 0 ? (
            <div style={note}>
              No choices yet. Nothing prints here until there are some.
              <div style={{ marginTop: '8px' }}>
                <button style={css?.smBtn?.()} onClick={(e) => { e.stopPropagation(); openOptions(true); }}>
                  Set up the choices
                </button>
              </div>
            </div>
          ) : (
            <div style={{ marginTop: '6px' }}>
              {optionGroups.map((g) => (
                <div key={g.id} style={{ marginBottom: '10px' }}>
                  {/* Same rule as the renderer: a group label identical to the
                      section heading is not printed twice. */}
                  {String(g.label || '').trim() !== String(b.text || '').trim() && (
                    <div style={{ ...common, fontWeight: 700,
                                  fontSize: `${(styles.h3?.size || 11.5) * 1.25}px` }}>{g.label}</div>
                  )}
                  <div style={{ fontSize: '11px', color: C?.muted }}>
                    {g.help_text?.trim() || (g.selection === 'multi' ? 'Choose any that apply.' : 'Choose one.')}
                  </div>
                  {optionRows.filter((o) => o.group_id === g.id).map((o) => (
                    <div key={o.id} style={{ display: 'flex', gap: '8px', marginTop: '7px' }}>
                      <div style={{ width: '11px', height: '11px', flex: '0 0 11px', marginTop: '3px',
                                    border: `1px solid ${C?.border}`, borderRadius: '2px' }} />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ ...common, fontWeight: 700 }}>
                          {o.label}{o.is_recommended ? '  (recommended)' : ''}
                        </div>
                        {optionPrice(o)
                          ? <div style={{ ...common, fontWeight: 700 }}>{optionPrice(o)}</div>
                          : o.billing_model_id
                            ? <div style={{ fontSize: '11px', color: C?.badgeAmber }}>
                                No price entered — nothing will print here
                              </div>
                            : null}
                        {(o.charges || []).filter((c) => c?.label && c?.amount != null).length > 0 && (
                          <div style={{ fontSize: '11px', color: C?.muted }}>
                            {(o.charges || []).filter((c) => c?.label && c?.amount != null)
                              .map((c) => `${c.label} ${money(c.amount)}`).join(' · ')}
                          </div>
                        )}
                        {o.body_text?.trim() && <div style={common}>{o.body_text.trim()}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
              <button style={{ ...(css?.smBtn?.() || {}), marginTop: '2px' }}
                      onClick={(e) => { e.stopPropagation(); openOptions(); }}>
                Edit pricing options
              </button>
            </div>
          )}
        </div>
      );
    }
    if (b.type === 'columns') {
      const cols = columnsOf(b);
      const fracs = tableColumnWidths(b, Math.max(cols.length, 1));
      return (
        <div style={{ display: 'flex', gap: `${b.gap || 18}px`, alignItems: 'flex-start',
                      marginTop: common.marginTop, marginBottom: common.marginBottom }}>
          {cols.map((col, ci) => (
            <div key={ci} style={{ flex: `0 0 calc(${fracs[ci] * 100}% - ${((b.gap || 18) * (cols.length - 1)) / cols.length}px)`,
                                   minWidth: 0 }}>
              {col.length === 0
                ? <div style={{ fontSize: '11px', color: C?.muted, fontStyle: 'italic',
                                border: `1px dashed ${C?.border}`, borderRadius: '6px', padding: '10px' }}>
                    Empty column
                  </div>
                : col.map((child) => (
                    <div key={child.id} onClick={(e) => { e.stopPropagation(); setSelectedId(child.id); }}
                         style={{ cursor: 'pointer',
                                  outline: selectedId === child.id ? `2px solid ${C?.accent}` : 'none',
                                  outlineOffset: '-1px', borderRadius: '4px',
                                  paddingLeft: '8px', marginLeft: '-10px' }}>
                      {previewBlock(child)}
                    </div>
                  ))}
            </div>
          ))}
        </div>
      );
    }
    if (b.type === 'table') {
      const grid = tableGrid(b);
      const cols = grid[0]?.length || 1;
      const fracs = tableColumnWidths(b, cols);
      const hdr = b.header_row !== false;
      const cell = (text, isHeader, striped) => ({
        border: `1px solid ${docColorCss(b.border_color || 'rule')}`,
        padding: '5px 7px',
        fontSize: `${(style.size || 10.5) * 1.25}px`,
        lineHeight: style.line || 1.4,
        fontWeight: isHeader ? 700 : (style.bold ? 700 : 400),
        background: isHeader
          ? (b.header_fill ? docColorCss(b.header_fill) : 'transparent')
          : (striped && b.stripe_fill ? docColorCss(b.stripe_fill) : 'transparent'),
        color: isHeader && b.header_text_color ? docColorCss(b.header_text_color) : common.color,
        verticalAlign: 'top',
      });
      return (
        <div style={{ marginTop: common.marginTop, marginBottom: common.marginBottom }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <colgroup>{fracs.map((f, i) => <col key={i} style={{ width: `${f * 100}%` }} />)}</colgroup>
            <tbody>
              {grid.map((row, r) => (
                <tr key={r}>
                  {row.map((c, ci) => (
                    <td key={ci} style={cell(c, hdr && r === 0, (r - (hdr ? 1 : 0)) % 2 === 1)}>
                      <InlineText text={c} resolve={resolve} deferred={deferred} C={C} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {fieldRow}
        </div>
      );
    }
    if (b.type === 'divider') {
      const dc = b.color || styles.divider?.color;
      return <div style={{ margin: '14px 0',
                           borderTop: `1px solid ${dc && dc !== 'rule' ? docColorCss(dc) : C?.border}` }} />;
    }
    if (b.type === 'spacer') return <div style={{ height: `${b.height || 18}px` }} />;
    if (b.type === 'signature_block') {
      // A column is a person: group consecutive fields by role, exactly as the
      // PDF renderer does, so the preview shows the real shape of the block.
      const groups = [];
      for (const f of fieldsHere) {
        const last = groups[groups.length - 1];
        if (last && (last[0].role_id ?? null) === (f.role_id ?? null)) last.push(f);
        else groups.push([f]);
      }
      // A field with no role is nobody's column -- it sits under them.
      const loose = groups.filter((g) => !g[0].role_id).flat();
      const columns = groups.filter((g) => g[0].role_id);
      const cols = Number(b.columns) === 1 ? 1 : 2;
      const headers = (b.column_headers || []).map((h) =>
        resolveLines(h, resolve).map((line) => line.map((r) => r.text).join('')).join(' ').trim());
      return (
        <div style={{ marginTop: '18px' }}>
          {b.label && <div style={{ ...common, fontWeight: 700 }}>{b.label}</div>}
          {fieldsHere.length === 0 ? (
            <div style={{ fontSize: '11px', color: templateMode ? C?.muted : C?.badgeAmber }}>
              {templateMode
                ? 'Signature lines appear here once a contract from this template has its signers.'
                : 'Nobody is set to sign here yet.'}
            </div>
          ) : (
            <>
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`,
                          gap: '12px 24px', marginTop: '10px' }}>
              {columns.map((group, gi) => (
                <div key={gi}>
                  {headers[gi % cols] && (
                    <div style={{ fontSize: '11px', fontWeight: 700, color: C?.textBright, marginBottom: '8px' }}>
                      {headers[gi % cols]}
                    </div>
                  )}
                  {group.map((f) => (
                    <div key={f.id} style={{ marginBottom: '10px' }}>
                      <div style={{ height: f.field_kind === 'signature' ? '30px' : '14px',
                                    borderBottom: `1px solid ${C?.border}` }} />
                      <div style={{ fontSize: '9px', color: C?.muted, marginTop: '3px' }}>{f.label}</div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
            {loose.map((f) => (
              <div key={f.id} style={{ marginTop: '10px' }}>
                <div style={{ height: '14px', borderBottom: `1px solid ${C?.border}` }} />
                <div style={{ fontSize: '9px', color: C?.muted, marginTop: '3px' }}>{f.label}</div>
              </div>
            ))}
            </>
          )}
        </div>
      );
    }
    if (b.type === 'bullet_list' || b.type === 'numbered_list') {
      return (
        <div style={common}>
          {(b.items || []).map((item, i) => (
            <div key={i} style={{ display: 'flex', gap: '8px' }}>
              <span style={{ color: C?.muted }}>{b.type === 'bullet_list' ? '•' : `${i + 1}.`}</span>
              <span><InlineText text={item} resolve={resolve} deferred={deferred} C={C} /></span>
            </div>
          ))}
          {fieldRow}
        </div>
      );
    }
    return (
      <div style={common}>
        {b.fill_mode === 'prompted' && !String(b.text ?? '').trim() ? (
          <span style={{ color: C?.muted, fontStyle: 'italic' }}>
            [{b.label || 'Filled when sending'}]
          </span>
        ) : (
          <InlineText text={b.text} resolve={resolve} deferred={deferred} C={C}
                      prefix={clauseNumbers[b.id] ? clauseNumberPrefix(clauseNumbers[b.id]) : null} />
        )}
        {fieldRow}
      </div>
    );
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 9800,
                  display: 'flex', flexDirection: 'column', padding: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: '14px', fontWeight: 700, color: C?.textBright }}>
            {templateMode ? `Template · ${templateName}` : `Write document · ${contract.title}`}
          </div>
          <div style={{ fontSize: '11px', color: C?.muted }}>
            Sections flow in order. Formatting comes from styles, so every contract you send looks the same.
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {dirty && <span style={{ fontSize: '11px', color: C?.badgeAmber }}>Unsaved changes</span>}
          <button style={{ ...(css?.btn?.('ghost') || {}), fontSize: '13px' }} onClick={() => setImporting(true)}>
            Import from Word
          </button>
          <button style={{ ...(css?.btn?.('primary') || {}), fontSize: '13px' }} disabled={busy} onClick={save}>
            {busy ? 'Saving…' : 'Save'}
          </button>
          <button style={{ ...(css?.btn?.('ghost') || {}), fontSize: '13px' }} onClick={closeEditor}>Close</button>
        </div>
      </div>

      {importing && (
        <ContractDocxImportModal
          orgId={contract.org_id} scopeId={contract.id} hasExisting={blocks.length > 0}
          css={css} C={C}
          onClose={() => setImporting(false)}
          onUse={(imported, report, provenance) => {
            setBlocks(imported);
            setSelectedId(null);
            setPageSetup(false);
            setImported({ report, provenance });
            setDirty(true);
            setImporting(false);
          }}
          onError={onError} />
      )}

      <div style={{ flex: 1, minHeight: 0, display: 'flex', gap: '12px' }}>
        {/* ── Sections ───────────────────────────────────────────── */}
        {!sectionsOpen ? (
          // Collapsed: a spine you can click back open, keeping the document
          // and its settings on screen without losing the way back.
          <button type="button" onClick={() => setSectionsOpen(true)} title="Show sections"
                  style={{ ...PANEL(C), width: '34px', flexShrink: 0, cursor: 'pointer',
                           alignItems: 'center', justifyContent: 'flex-start', paddingTop: '12px',
                           gap: '10px', color: C?.muted, fontSize: '11px' }}>
            <span style={{ fontSize: '13px' }}>›</span>
            <span style={{ writingMode: 'vertical-rl', letterSpacing: '0.08em' }}>
              Sections ({blocks.length})
            </span>
          </button>
        ) : (
        <div style={{ ...PANEL(C), width: '260px', flexShrink: 0 }}>
          <div style={{ padding: '10px 12px', borderBottom: `1px solid ${C?.border}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        gap: '8px', fontSize: '11px', fontWeight: 700, color: C?.textBright }}>
            <span>Sections <span style={{ color: C?.muted, fontWeight: 400 }}>({blocks.length})</span></span>
            <button type="button" onClick={() => setSectionsOpen(false)} title="Hide sections"
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer',
                             color: C?.muted, fontSize: '13px', padding: 0, lineHeight: 1 }}>‹</button>
          </div>

          <div style={{ padding: '8px 8px 0' }}>
            <div style={{ position: 'relative' }}>
              <input value={query} onChange={(e) => setQuery(e.target.value)}
                     onKeyDown={(e) => { if (e.key === 'Escape') { setQuery(''); e.currentTarget.blur(); } }}
                     placeholder="Find a section…"
                     style={{ ...(css?.inp || {}), fontSize: '11px', padding: '5px 24px 5px 8px' }} />
              {query && (
                <button type="button" onClick={() => setQuery('')} title="Clear"
                        style={{ position: 'absolute', right: '4px', top: '50%', transform: 'translateY(-50%)',
                                 background: 'transparent', border: 'none', cursor: 'pointer',
                                 color: C?.muted, fontSize: '13px', lineHeight: 1, padding: '2px 4px' }}>×</button>
              )}
            </div>
            {filtering && (
              <div style={{ fontSize: '10px', color: C?.muted, margin: '5px 0 2px', lineHeight: 1.5 }}>
                {search.size === 0
                  ? 'Nothing matches.'
                  : `${search.size} of ${blocks.length} section${blocks.length === 1 ? '' : 's'} · reordering is off while searching`}
              </div>
            )}
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
            {blocks.map((b, i) => {
              // NOTE: `i` stays the block's REAL index in `blocks`, filtered or
              // not. move/duplicate/drop all take an index, so filtering the
              // array first would quietly operate on the wrong section.
              if (filtering && !search.has(b.id)) return null;
              const attached = fields.filter((f) => f.block_id === b.id).length;
              const hitChild = filtering ? search.get(b.id) : null;
              return (
                <div key={b.id} draggable={!filtering}
                     ref={(el) => { rowRefs.current[b.id] = el; }}
                     onDragStart={() => { dragFrom.current = i; setDragOver(null); }}
                     onDragOver={(e) => { e.preventDefault(); if (dragOver !== i) setDragOver(i); }}
                     onDragLeave={() => { if (dragOver === i) setDragOver(null); }}
                     onDragEnd={() => { dragFrom.current = null; setDragOver(null); }}
                     onDrop={() => { if (!filtering) move(dragFrom.current, i); dragFrom.current = null; setDragOver(null); }}
                     onClick={() => setSelectedId(hitChild || b.id)}
                     style={{ padding: '7px 9px', marginBottom: '5px', borderRadius: '6px', cursor: 'grab',
                              // Longhand on every side: mixing `border` with a
                              // conditional `borderTop` makes React warn on
                              // every render once a drag starts, which buries
                              // anything real in the console.
                              borderRightWidth: '1px', borderRightStyle: 'solid',
                              borderBottomWidth: '1px', borderBottomStyle: 'solid',
                              borderLeftWidth: '1px', borderLeftStyle: 'solid',
                              borderRightColor: selectedId === b.id ? C?.accent : C?.border,
                              borderBottomColor: selectedId === b.id ? C?.accent : C?.border,
                              borderLeftColor: selectedId === b.id ? C?.accent : C?.border,
                              // Where it would land, while a section is dragged over it.
                              borderTopStyle: 'solid',
                              borderTopWidth: dragOver === i && dragFrom.current !== null && dragFrom.current !== i ? '2px' : '1px',
                              borderTopColor: dragOver === i && dragFrom.current !== null && dragFrom.current !== i
                                ? C?.accent : (selectedId === b.id ? C?.accent : C?.border),
                              background: selectedId === b.id ? `${C?.accent}14` : 'transparent' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '6px', alignItems: 'center' }}>
                    <span style={{ fontSize: '10px', color: C?.muted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      {/* A visible grab handle: the row has always been
                          draggable, but nothing said so. */}
                      {!filtering && (
                        <span title="Drag to move" style={{ cursor: 'grab', marginRight: '5px', letterSpacing: '-1px' }}>⠿</span>
                      )}
                      {clauseNumbers[b.id] && (
                        <span style={{ color: C?.accent, marginRight: '4px' }}>{clauseNumbers[b.id]}</span>
                      )}
                      {BLOCK_TYPES[b.type]?.label || b.type}
                    </span>
                    <span style={{ display: 'flex', gap: '2px' }}>
                      <button style={css?.smBtn?.()} title="Duplicate"
                              onClick={(e) => { e.stopPropagation(); duplicateBlock(i); }}>⧉</button>
                      {!filtering && (<>
                        <button style={css?.smBtn?.()} title="Move up"
                                onClick={(e) => { e.stopPropagation(); move(i, i - 1); }}>↑</button>
                        <button style={css?.smBtn?.()} title="Move down"
                                onClick={(e) => { e.stopPropagation(); move(i, i + 1); }}>↓</button>
                      </>)}
                    </span>
                  </div>
                  <div style={{ fontSize: '12px', color: C?.text, marginTop: '2px',
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {/* blockPlainText, not b.text: the raw string carries the
                        **bold** and *italic* markers, and a list of search
                        results reading "**CONFIDENTIAL**" is just noise. It
                        also covers list items, table cells and columns, which
                        the old expression did not. */}
                    {b.fill_mode === 'prompted'
                      ? (b.label || 'Filled when sending')
                      : (blockPlainText(b).replace(/\s+/g, ' ').trim() || b.label || '—')}
                  </div>
                  <div style={{ display: 'flex', gap: '6px', marginTop: '3px' }}>
                    {b.fill_mode === 'prompted' && (
                      <span style={{ fontSize: '9px', color: C?.accent }}>filled when sending</span>
                    )}
                    {attached > 0 && (
                      <span style={{ fontSize: '9px', color: C?.muted }}>{attached} field{attached === 1 ? '' : 's'}</span>
                    )}
                    {hitChild && hitChild !== b.id && (
                      <span style={{ fontSize: '9px', color: C?.accent }}>match inside</span>
                    )}
                  </div>
                </div>
              );
            })}

          </div>

          {/* PINNED, not the last row of the list. It used to sit at the bottom
              of the scroll, so adding a section to a 75-section contract meant
              scrolling past all of them to find the button. */}
          <div style={{ borderTop: `1px solid ${C?.border}`, padding: '8px', flexShrink: 0 }}>
            {adding ? (
              <div style={{ padding: '8px', border: `1px solid ${C?.accent}`, borderRadius: '6px',
                            maxHeight: '260px', overflowY: 'auto' }}>
                {BLOCK_ORDER.map((type) => (
                  <button key={type} onClick={() => addBlock(type)}
                          style={{ ...smallBtn(false), display: 'block', width: '100%', textAlign: 'left', marginBottom: '4px' }}>
                    {BLOCK_TYPES[type].label}
                  </button>
                ))}
                <button style={{ ...(css?.btn?.('ghost') || {}), fontSize: '11px' }} onClick={() => setAdding(false)}>Cancel</button>
              </div>
            ) : (
              <button style={{ ...smallBtn(false), width: '100%' }} onClick={() => setAdding(true)}>
                + Add a section{selected ? ' below the selected one' : ''}
              </button>
            )}
          </div>
        </div>
        )}

        {/* ── Preview ────────────────────────────────────────────── */}
        <div style={{ ...PANEL(C), flex: 1 }}>
          <div style={{ padding: '10px 12px', borderBottom: `1px solid ${C?.border}`, display: 'flex',
                        justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '11px', fontWeight: 700, color: C?.textBright }}>Preview</span>
              {!templateMode && (
                <div style={{ display: 'flex', gap: '3px' }}>
                  <button style={smallBtn(mode === 'writing')} onClick={() => setMode('writing')}>Writing</button>
                  <button style={smallBtn(mode === 'pages')} onClick={() => setMode('pages')}>
                    Pages
                  </button>
                </div>
              )}
              {mode === 'pages' && (pdfBusy
                ? <span style={{ fontSize: '10px', color: C?.muted }}>Rendering…</span>
                : <button style={smallBtn(false)} onClick={() => setPdfStamp((n) => n + 1)}
                          title="Render these pages again">Refresh</button>)}
            </div>
            <span style={{ fontSize: '10px', color: C?.muted, textAlign: 'right' }}>
              {mode === 'pages'
                ? (pdfStale
                    ? 'The real PDF — of the last saved version (see the note).'
                    : 'The real PDF, rendered from what is on screen now — including anything unsaved.')
                : templateMode
                  ? 'Merge fields stand in example details; each contract fills in its own. Page breaks in the PDF may fall elsewhere.'
                  : 'Merge fields show this contract’s own details. Switch to Pages to see where they actually fall.'}
            </span>
          </div>
          {mode === 'pages' ? (
            <div style={{ flex: 1, overflowY: 'auto', padding: '18px', background: C?.bg }}>
              {pdfError ? (
                <div style={{ padding: '14px', borderRadius: '8px', fontSize: '12px', lineHeight: 1.6,
                              border: `1px solid ${C?.danger}`, color: C?.danger, marginBottom: '14px' }}>
                  {pdfError}
                  <div style={{ color: C?.muted, marginTop: '6px' }}>
                    This is the same check that runs when you send, so it has to be put right either way.
                  </div>
                </div>
              ) : null}
              {pdfStale && (
                <div style={{ padding: '10px 12px', borderRadius: '8px', fontSize: '11px', lineHeight: 1.6,
                              border: `1px solid ${C?.badgeAmber || C?.border}`, color: C?.badgeAmber || C?.muted,
                              marginBottom: '12px' }}>
                  These are the pages of the <strong>last saved</strong> version — this TimeKeep is newer than
                  the renderer it is talking to, so unsaved edits are not shown. Save to see them, or redeploy
                  <code style={{ margin: '0 4px' }}>preview-contract-document</code>.
                </div>
              )}
              {pdfWarnings.length > 0 && (
                <div style={{ fontSize: '11px', color: C?.badgeAmber || C?.muted, marginBottom: '12px', lineHeight: 1.6 }}>
                  {pdfWarnings.map((w, i) => <div key={i}>{w.message}</div>)}
                </div>
              )}
              {pdfUrl && !pdfError
                ? <ContractPdf url={pdfUrl} C={C} maxWidth={620} />
                : !pdfError && <div style={{ padding: '40px', textAlign: 'center', color: C?.muted, fontSize: '13px' }}>
                    Rendering the document…
                  </div>}
            </div>
          ) : (
          <div style={{ flex: 1, overflowY: 'auto', padding: '28px 34px', background: C?.bg }}>
            {headerUrl && (
              <div style={{ marginBottom: '18px',
                            textAlign: (layout.logo?.align || 'left') === 'left' ? 'left' : layout.logo?.align }}>
                <img src={headerUrl} alt="" style={{
                  width: `${Math.round(((layout.logo?.width_pct) || (layout.logo?.width || 120) / 468) * 100)}%`,
                  maxWidth: '100%', display: 'inline-block' }} />
              </div>
            )}
            {layout.header_text && (
              <div style={{ fontSize: '9px', color: C?.muted, marginBottom: '12px' }}>{layout.header_text}</div>
            )}
            {rendered.length === 0 && (
              <div style={{ fontSize: '12px', color: C?.muted, fontStyle: 'italic' }}>
                Nothing in the document yet — add a section to start.
              </div>
            )}
            {rendered.map((b, bi) => (
              <div key={`wrap-${b.id}`}>
              <InsertGap types={BLOCK_ORDER} labels={BLOCK_LABELS}
                         onPick={(t) => insertAt(bi, t)} css={css} C={C} />
              {/* OUTLINE, not a border. A border changes the element's size, so
                  selecting a block nudged the text a pixel and reflowed the
                  paragraph under it; outline is drawn outside the box and moves
                  nothing. A // comment here is JSX CHILDREN and renders as text
                  in the document -- which is exactly what it did until a
                  screenshot caught it sitting in the middle of the agreement. */}
              <div onClick={() => setSelectedId(b.id)}
                   ref={(el) => { previewRefs.current[b.id] = el; }}
                   style={{ cursor: 'pointer', borderRadius: '6px', position: 'relative',
                            padding: '2px 10px', margin: '0 -12px',
                            outline: selectedId === b.id ? `2px solid ${C?.accent}` : 'none',
                            outlineOffset: '-1px',
                            background: selectedId === b.id ? `${C?.accent}0F` : 'transparent' }}>
                {/* On the SELECTED block only. Showing these on hover would mean
                    tracking a hovered id in the editor, which re-renders every
                    block on every mouse move; showing them always would turn a
                    preview of the agreement into a control panel. You select a
                    block to work on it, and then the controls are on it. */}
                {selectedId === b.id && (
                  <div style={{ position: 'absolute', top: '-10px', right: '2px', display: 'flex', gap: '3px',
                                background: C?.surface, border: `1px solid ${C?.border}`,
                                borderRadius: '5px', padding: '2px' }}>
                    <button title="Duplicate" style={{ ...(css?.smBtn?.() || {}), fontSize: '10px' }}
                            onClick={(e) => { e.stopPropagation(); duplicateBlock(bi); }}>⧉</button>
                    <button title="Move up" disabled={bi === 0}
                            style={{ ...(css?.smBtn?.() || {}), fontSize: '10px', opacity: bi === 0 ? 0.35 : 1 }}
                            onClick={(e) => { e.stopPropagation(); move(bi, bi - 1); }}>↑</button>
                    <button title="Move down" disabled={bi === rendered.length - 1}
                            style={{ ...(css?.smBtn?.() || {}), fontSize: '10px',
                                     opacity: bi === rendered.length - 1 ? 0.35 : 1 }}
                            onClick={(e) => { e.stopPropagation(); move(bi, bi + 1); }}>↓</button>
                  </div>
                )}
                {previewBlock(b)}
              </div>
              </div>
            ))}
            <InsertGap types={BLOCK_ORDER} labels={BLOCK_LABELS}
                       onPick={(t) => insertAt(rendered.length, t)} css={css} C={C} />
          </div>
          )}
        </div>

        {/* ── Properties ─────────────────────────────────────────── */}
        <div style={{ ...PANEL(C), width: '300px', flexShrink: 0 }}>
          <div style={{ padding: '10px 12px', borderBottom: `1px solid ${C?.border}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
            <span style={{ fontSize: '11px', fontWeight: 700, color: C?.textBright }}>
              {pageSetup ? 'Page setup' : textStyles ? 'Text styles' : selected ? (
                /* A breadcrumb rather than a back button: it says where you are
                   as well as how to leave, which is the actual confusion. */
                selectedParent ? (
                  <>
                    {/* The arrow points LEFT, at the thing it goes back to. A
                        trailing › reads as going further in, which is the
                        opposite of what this control does. */}
                    <button type="button" onClick={() => setSelectedId(selectedParent.id)}
                            title={`Back to ${BLOCK_TYPES[selectedParent.type]?.label || 'Side by side'}`}
                            style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
                                     color: C?.accent, fontSize: '11px', fontWeight: 700 }}>
                      ‹ {BLOCK_TYPES[selectedParent.type]?.label || 'Side by side'}
                    </button>
                    <span style={{ color: C?.muted, fontWeight: 400 }}> · </span>
                    {BLOCK_TYPES[selected.type]?.label || selected.type}
                  </>
                ) : (BLOCK_TYPES[selected.type]?.label || selected.type)
              ) : 'Nothing selected'}
            </span>
            <div style={{ display: 'flex', gap: '4px' }}>
              <button style={smallBtn(textStyles)}
                      onClick={() => { setTextStyles((v) => !v); setPageSetup(false); }}>
                {textStyles ? 'Done' : 'Text styles'}
              </button>
              <button style={smallBtn(pageSetup)}
                      onClick={() => { setPageSetup((v) => !v); setTextStyles(false); }}>
                {pageSetup ? 'Done' : 'Page setup'}
              </button>
            </div>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '12px' }}>
            {textStyles && (
              <>
                <div style={{ fontSize: '11px', color: C?.muted, lineHeight: 1.6, marginBottom: '12px' }}>
                  The color each kind of text takes by default in this document. A single
                  section can still be given its own color from its own settings.
                </div>
                {STYLE_NAMES.map((name) => (
                  <div key={name} style={{ marginBottom: '14px' }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '2px' }}>
                      <span style={{ fontSize: '11px', fontWeight: 700, color: C?.textBright }}>
                        {STYLE_LABELS[name] || name}
                      </span>
                      <span style={{
                        fontSize: `${Math.min((styles[name]?.size || 10.5) * 1.1, 17)}px`,
                        fontWeight: styles[name]?.bold ? 700 : 400,
                        fontStyle: styles[name]?.italic ? 'italic' : 'normal',
                        color: docColorCss(styles[name]?.color || DEFAULT_STYLES[name]?.color),
                      }}>
                        Sample
                      </span>
                    </div>
                    <ColorField
                      value={isHexColor(styles[name]?.color) ? styles[name].color : null}
                      fallback={DEFAULT_STYLES[name]?.color || 'ink'}
                      onChange={(v) => setStyleColor(name, v)}
                      C={C} css={css} />
                  </div>
                ))}
                <div style={{ marginBottom: '14px' }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: C?.textBright, marginBottom: '2px' }}>
                    Divider line
                  </div>
                  <ColorField
                    value={isHexColor(styles.divider?.color) ? styles.divider.color : null}
                    fallback={DEFAULT_STYLES.divider?.color || 'rule'}
                    onChange={(v) => setStyleColor('divider', v)}
                    C={C} css={css} />
                </div>
                <div style={{ fontSize: '10px', color: C?.muted, lineHeight: 1.6 }}>
                  Colors apply to this {templateMode ? 'template' : 'document'} only. A contract
                  made from a template keeps the template's colors.
                </div>
              </>
            )}

            {pageSetup && (
              <>
                <div style={lbl}>
                  <TKTooltip text="The image drawn at the top of the document. Your organization logo is used unless you upload a letterhead just for contracts.">
                    Header image ⓘ
                  </TKTooltip>
                </div>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '8px' }}>
                  {[['org', 'Organization logo'], ['upload', 'Letterhead'], ['none', 'None']].map(([v, label]) => (
                    <button key={v} style={smallBtn((layout.logo?.source || 'org') === v)}
                            onClick={() => setLogo({ source: v, show: v !== 'none' })}>{label}</button>
                  ))}
                </div>

                {(layout.logo?.source || 'org') === 'upload' && (
                  <div style={{ marginBottom: '10px' }}>
                    <input type="file" accept="image/png,image/jpeg" disabled={uploading}
                           style={{ fontSize: '11px', color: C?.text }}
                           onChange={(e) => uploadHeader(e.target.files?.[0])} />
                    <div style={{ fontSize: '10px', color: C?.muted, marginTop: '4px', lineHeight: 1.5 }}>
                      {uploading ? 'Uploading…'
                        : layout.logo?.path ? 'Replacing it uploads a new image; documents already sent keep the one they were frozen with.'
                        : 'PNG or JPEG, up to 2MB. Wider images read best as a letterhead.'}
                    </div>
                  </div>
                )}

                {(layout.logo?.source || 'org') !== 'none' && (
                  <>
                    <div style={lbl}>Width</div>
                    <input type="range" min={20} max={100} step={5}
                           value={Math.round(((layout.logo?.width_pct) || (layout.logo?.width || 120) / 468) * 100)}
                           onChange={(e) => setLogo({ width_pct: Number(e.target.value) / 100 })}
                           style={{ width: '100%' }} />
                    <div style={{ fontSize: '10px', color: C?.muted, marginBottom: '8px' }}>
                      {Math.round(((layout.logo?.width_pct) || (layout.logo?.width || 120) / 468) * 100)}% of the text width
                    </div>

                    <div style={lbl}>Position</div>
                    <div style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
                      {['left', 'center', 'right'].map((a) => (
                        <button key={a} style={smallBtn((layout.logo?.align || 'left') === a)}
                                onClick={() => setLogo({ align: a })}>
                          {a[0].toUpperCase() + a.slice(1)}
                        </button>
                      ))}
                    </div>

                    <label style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '10px',
                                    fontSize: '12px', color: C?.text, cursor: 'pointer' }}>
                      <input type="checkbox" checked={!!layout.logo?.repeat}
                             onChange={(e) => setLogo({ repeat: e.target.checked })} />
                      Repeat on every page
                    </label>
                  </>
                )}

                <div style={lbl}>
                  <TKTooltip text="A cover carries no running header, no footer and no page number, and numbering starts on the page after it — so the first page of the agreement itself is Page 1.">
                    Cover page ⓘ
                  </TKTooltip>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '4px',
                                fontSize: '12px', color: C?.text, cursor: 'pointer' }}>
                  <input type="checkbox" checked={layout.cover_page === true}
                         onChange={(e) => { setLayout((l) => ({ ...l, cover_page: e.target.checked })); setDirty(true); }} />
                  Page 1 is a cover
                </label>
                {!hasCover && (
                  <button style={{ ...(css?.btn?.('ghost') || {}), fontSize: '11px', marginBottom: '10px' }}
                          onClick={insertCover}>
                    Insert cover page
                  </button>
                )}
                {hasCover && (
                  <div style={{ fontSize: '10px', color: C?.muted, marginBottom: '10px', lineHeight: 1.5 }}>
                    The sections above the first page break are the cover. Edit them like any others.
                  </div>
                )}

                <div style={lbl}>Running header</div>
                <input style={css?.inp} value={layout.header_text || ''} placeholder="Optional, e.g. Acme Corp — Confidential"
                       {...tokens.target((v) => { setLayout((l) => ({ ...l, header_text: v })); setDirty(true); })}
                       onChange={(e) => { setLayout((l) => ({ ...l, header_text: e.target.value })); setDirty(true); }} />

                {layout.cover_page === true ? (
                  <div style={{ fontSize: '10px', color: C?.muted, marginTop: '4px', lineHeight: 1.5 }}>
                    Not shown on the cover.
                  </div>
                ) : (
                  <>
                    <div style={{ ...lbl, marginTop: '8px' }}>On page 1</div>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      {[['same', 'Same'], ['different', 'Different'], ['none', 'None']].map(([v, label]) => (
                        <button key={v} style={smallBtn((layout.header_first_mode || 'same') === v)}
                                onClick={() => { setLayout((l) => ({ ...l, header_first_mode: v })); setDirty(true); }}>
                          {label}
                        </button>
                      ))}
                    </div>
                    {(layout.header_first_mode || 'same') === 'different' && (
                      <input style={{ ...(css?.inp || {}), marginTop: '6px' }} placeholder="Header for page 1"
                             value={layout.header_text_first || ''}
                             {...tokens.target((v) => { setLayout((l) => ({ ...l, header_text_first: v })); setDirty(true); })}
                             onChange={(e) => { setLayout((l) => ({ ...l, header_text_first: e.target.value })); setDirty(true); }} />
                    )}
                    <div style={{ fontSize: '10px', color: C?.muted, marginTop: '4px', lineHeight: 1.5 }}>
                      The header image follows its own rule above — page 1 only, unless you tick Repeat.
                    </div>
                  </>
                )}

                <div style={{ ...lbl, marginTop: '10px' }}>Running footer</div>
                <input style={css?.inp} value={layout.footer_text || ''} placeholder="Optional"
                       {...tokens.target((v) => { setLayout((l) => ({ ...l, footer_text: v })); setDirty(true); })}
                       onChange={(e) => { setLayout((l) => ({ ...l, footer_text: e.target.value })); setDirty(true); }} />

                <MergeFieldBank resolve={resolve} onInsert={tokens.insert} css={css} C={C}
                  note="Click one to drop it into the header or footer at the cursor." />

                <div style={{ borderTop: `1px solid ${C?.border}`, margin: '16px 0 12px' }} />
                <label style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '8px',
                                fontSize: '12px', color: C?.text, cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!layout.clause_numbering?.enabled}
                         onChange={(e) => {
                           setLayout((l) => ({ ...l, clause_numbering: { ...(l.clause_numbering || {}), enabled: e.target.checked } }));
                           setDirty(true);
                         }} />
                  <TKTooltip text="Numbers your clauses from their position in the document. Cross-references follow them, so inserting a clause renumbers everything that points at it.">
                    Number clauses automatically ⓘ
                  </TKTooltip>
                </label>
                {layout.clause_numbering?.enabled && (
                  <div style={{ marginBottom: '14px' }}>
                    <div style={lbl}>Start numbering at</div>
                    <select style={css?.sel} value={layout.clause_numbering?.start_level || 'h2'}
                            onChange={(e) => {
                              setLayout((l) => ({ ...l, clause_numbering: { ...(l.clause_numbering || {}), start_level: e.target.value } }));
                              setDirty(true);
                            }}>
                      {HEADING_LEVELS.map((h) => <option key={h} value={h}>{STYLE_LABELS[h]}</option>)}
                    </select>
                    <div style={{ fontSize: '10px', color: C?.muted, marginTop: '4px', lineHeight: 1.5 }}>
                      Headings above this level are left alone — a document’s own title is usually
                      Heading 1 and should not become clause 1.
                    </div>
                  </div>
                )}

                <label style={{ display: 'flex', alignItems: 'center', gap: '7px', marginTop: '10px',
                                fontSize: '12px', color: C?.text, cursor: 'pointer' }}>
                  <input type="checkbox" checked={layout.page_numbers !== false}
                         onChange={(e) => { setLayout((l) => ({ ...l, page_numbers: e.target.checked })); setDirty(true); }} />
                  Number the pages
                </label>
              </>
            )}

            {!pageSetup && !textStyles && !selected && (
              <div style={{ fontSize: '12px', color: C?.muted, lineHeight: 1.6 }}>
                Pick a section to edit its text and settings.
              </div>
            )}

            {!pageSetup && !textStyles && selected && BLOCK_TYPES[selected.type]?.text && selected.fill_mode !== 'prompted' && (
              <div style={{ marginBottom: '10px' }}>
                <div style={lbl}>Text</div>
                <RichTextBox value={selected.text || ''} rows={6} css={css} C={C}
                             title={`${BLOCK_TYPES[selected.type]?.label || 'Section'} text`}
                             target={tokens.target((v) => update(selected.id, { text: v }))}
                             onChange={(v) => update(selected.id, { text: v })} />
                <div style={{ fontSize: '10px', color: C?.muted, marginTop: '4px' }}>
                  Select text and press B or I, or type **bold** and *italic*. Merge fields below.
                </div>
                <MergeFieldBank resolve={resolve} onInsert={tokens.insert} css={css} C={C} />
                <CrossRefPicker sections={numberedSections} onInsert={tokens.insert}
                                currentId={selected.id} C={C} css={css} />
              </div>
            )}

            {!pageSetup && !textStyles && selected && BLOCK_TYPES[selected.type]?.items && (
              <div style={{ marginBottom: '10px' }}>
                <div style={lbl}>Items — one per line</div>
                <RichTextBox value={(selected.items || []).join('\n')} rows={6} css={css} C={C}
                             title="List items — one per line"
                             target={tokens.target((v) => update(selected.id, { items: v.split('\n') }))}
                             onChange={(v) => update(selected.id, { items: v.split('\n') })} />
                <MergeFieldBank resolve={resolve} onInsert={tokens.insert} css={css} C={C} />
              </div>
            )}

            {!pageSetup && !textStyles && selected && BLOCK_TYPES[selected.type]?.style && (
              <div style={{ marginBottom: '10px' }}>
                <div style={lbl}>Style</div>
                <select style={css?.sel} value={selected.style || 'body'}
                        onChange={(e) => update(selected.id, { style: e.target.value })}>
                  {STYLE_NAMES.map((s) => <option key={s} value={s}>{STYLE_LABELS[s] || s}</option>)}
                </select>
              </div>
            )}

            {!pageSetup && !textStyles && selected && layout.clause_numbering?.enabled
              && HEADING_LEVELS.includes(blockStyleName(selected)) && (
              <div style={{ marginBottom: '10px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '7px',
                                fontSize: '12px', color: C?.text, cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!selected.unnumbered}
                         onChange={(e) => update(selected.id, { unnumbered: e.target.checked })} />
                  Leave this section out of the numbering
                </label>
                {clauseNumbers[selected.id] && (
                  <div style={{ fontSize: '10px', color: C?.muted, marginTop: '4px' }}>
                    Currently {clauseNumberPrefix(clauseNumbers[selected.id]).trim()}
                  </div>
                )}
                {/* Turning numbering on under wording that already carries typed
                    numbers reads as "1. 1. Purpose". Say so, rather than editing
                    somebody's words for them. */}
                {clauseNumbers[selected.id] && /^\s*\**\s*\d+(\.\d+)*[.)]?\s/.test(selected.text || '') && (
                  <div style={{ fontSize: '10px', color: C?.badgeAmber || C?.muted, marginTop: '6px', lineHeight: 1.5 }}>
                    This heading already starts with a number of its own, so it will read
                    “{clauseNumberPrefix(clauseNumbers[selected.id])}{(selected.text || '').replace(/^\s*\**\s*/, '').slice(0, 26)}…”.
                    Remove the typed number, or leave this section out of the numbering.
                  </div>
                )}
              </div>
            )}

            {/* One section in its own color, without redefining the style. */}
            {!pageSetup && !textStyles && selected
              && (BLOCK_TYPES[selected.type]?.text || BLOCK_TYPES[selected.type]?.items) && (
              <ColorField
                label="Text color"
                hint="Leave it on Default and this section follows the Text styles panel, so changing the style later moves this too."
                value={isHexColor(selected.color) ? selected.color : null}
                fallback={styles[selected.style || 'body']?.color || 'ink'}
                onChange={(v) => update(selected.id, { color: v })}
                C={C} css={css} />
            )}

            {!pageSetup && !textStyles && selected?.type === 'table' && (() => {
              const grid = tableGrid(selected);
              const cols = grid[0]?.length || 1;
              const setGrid = (rows) => update(selected.id, { rows });
              const setCell = (r, c, v) => {
                const next = grid.map((row) => row.slice());
                next[r][c] = v;
                setGrid(next);
              };
              const addRow = () => setGrid([...grid, Array.from({ length: cols }, () => '')]);
              const delRow = (r) => setGrid(grid.filter((_, i) => i !== r));
              const addCol = () => {
                setGrid(grid.map((row) => [...row, '']));
                // Widths are per column, so they have to grow with it or the
                // stored list stops matching and silently falls back to equal.
                if (Array.isArray(selected.col_widths)) {
                  update(selected.id, { col_widths: [...selected.col_widths, 1] });
                }
              };
              const delCol = (c) => {
                if (cols <= 1) return;
                setGrid(grid.map((row) => row.filter((_, i) => i !== c)));
                if (Array.isArray(selected.col_widths)) {
                  update(selected.id, { col_widths: selected.col_widths.filter((_, i) => i !== c) });
                }
              };
              const pasteGrid = (text) => {
                const rows = String(text || '').replace(/\r/g, '').split('\n')
                  .filter((l) => l.trim() !== '')
                  .map((l) => l.split('\t'));
                if (!rows.length) return;
                const width = Math.max(...rows.map((r) => r.length));
                setGrid(rows.map((r) => { const o = r.slice(); while (o.length < width) o.push(''); return o; }));
                update(selected.id, { col_widths: null });
              };
              return (
                <>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '10px',
                                  fontSize: '12px', color: C?.text, cursor: 'pointer' }}>
                    <input type="checkbox" checked={selected.header_row !== false}
                           onChange={(e) => update(selected.id, { header_row: e.target.checked })} />
                    <TKTooltip text="The first row is treated as a heading: bold, filled, and repeated at the top of every page the table runs onto.">
                      First row is a header ⓘ
                    </TKTooltip>
                  </label>

                  <div style={lbl}>Cells</div>
                  <div style={{ marginBottom: '8px' }}>
                    {grid.map((row, r) => (
                      <div key={r} style={{ display: 'flex', gap: '3px', marginBottom: '3px', alignItems: 'center' }}>
                        {row.map((c, ci) => (
                          <input key={ci} value={c}
                                 placeholder={selected.header_row !== false && r === 0 ? 'Heading' : ''}
                                 {...tokens.target((v) => setCell(r, ci, v))}
                                 onChange={(e) => setCell(r, ci, e.target.value)}
                                 style={{ ...(css?.inp || {}), flex: 1, minWidth: 0, padding: '4px 6px', fontSize: '11px' }} />
                        ))}
                        <button onClick={() => delRow(r)} title="Remove this row"
                                style={{ background: 'none', border: 'none', color: C?.muted,
                                         cursor: 'pointer', fontSize: '13px', padding: '0 2px' }}>×</button>
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', marginBottom: '12px' }}>
                    <button style={smallBtn(false)} onClick={addRow}>+ Row</button>
                    <button style={smallBtn(false)} onClick={addCol}>+ Column</button>
                    <button style={smallBtn(false)} onClick={() => delCol(cols - 1)} disabled={cols <= 1}>− Column</button>
                  </div>

                  <div style={lbl}>
                    <TKTooltip text="Paste straight from Excel, Numbers or Google Sheets. Tabs separate the columns, so the table is built in one step rather than cell by cell.">
                      Paste from a spreadsheet ⓘ
                    </TKTooltip>
                  </div>
                  <textarea rows={3} placeholder={'Service\tFee\nAdvisory\t$2,000'}
                            onPaste={(e) => { e.preventDefault(); pasteGrid(e.clipboardData.getData('text')); }}
                            onChange={() => {}}
                            value=""
                            style={{ ...(css?.inp || {}), resize: 'vertical', fontFamily: 'ui-monospace, monospace',
                                     fontSize: '11px', marginBottom: '4px' }} />
                  <div style={{ fontSize: '10px', color: C?.muted, marginBottom: '12px', lineHeight: 1.5 }}>
                    Pasting replaces every cell in this table.
                  </div>

                  <div style={lbl}>Column widths</div>
                  <div style={{ display: 'flex', gap: '4px', marginBottom: '4px' }}>
                    {tableColumnWidths(selected, cols).map((f, i) => (
                      <input key={i} type="number" min="1" step="1"
                             value={Array.isArray(selected.col_widths) ? selected.col_widths[i] : Math.round(f * 100)}
                             onChange={(e) => {
                               const base = Array.isArray(selected.col_widths)
                                 ? selected.col_widths.slice()
                                 : tableColumnWidths(selected, cols).map((w) => Math.round(w * 100));
                               base[i] = Number(e.target.value) || 1;
                               update(selected.id, { col_widths: base });
                             }}
                             style={{ ...(css?.inp || {}), flex: 1, minWidth: 0, padding: '4px 6px', fontSize: '11px' }} />
                    ))}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                    <span style={{ fontSize: '10px', color: C?.muted, lineHeight: 1.5, flex: 1 }}>
                      Relative, not percentages — 3 and 1 makes the first column three times the second.
                    </span>
                    {Array.isArray(selected.col_widths) && (
                      <button style={smallBtn(false)} onClick={() => update(selected.id, { col_widths: null })}>Even</button>
                    )}
                  </div>

                  <ColorField label="Header fill" value={isHexColor(selected.header_fill) ? selected.header_fill : null}
                              fallback={selected.header_fill || 'rule'}
                              onChange={(v) => update(selected.id, { header_fill: v })} C={C} css={css} />
                  <ColorField label="Header text" value={isHexColor(selected.header_text_color) ? selected.header_text_color : null}
                              fallback={styles[selected.style || 'body']?.color || 'ink'}
                              onChange={(v) => update(selected.id, { header_text_color: v })} C={C} css={css} />
                  <ColorField label="Row shading"
                              hint="Fills every other row, which makes a wide table far easier to read across. Leave it off for a plain table."
                              value={isHexColor(selected.stripe_fill) ? selected.stripe_fill : null}
                              fallback={'rule'}
                              onChange={(v) => update(selected.id, { stripe_fill: v })} C={C} css={css} />
                  <ColorField label="Borders" value={isHexColor(selected.border_color) ? selected.border_color : null}
                              fallback={selected.border_color || 'rule'}
                              onChange={(v) => update(selected.id, { border_color: v })} C={C} css={css} />
                </>
              );
            })()}

            {!pageSetup && !textStyles && selected?.type === 'columns' && (() => {
              const cols = columnsOf(selected);
              const setCols = (next) => update(selected.id, { cols: next });
              const addColumn = () => {
                setCols([...cols, []]);
                if (Array.isArray(selected.col_widths)) update(selected.id, { col_widths: [...selected.col_widths, 1] });
              };
              const dropColumn = (ci) => {
                if (cols.length <= 2) return;
                // Whatever was in it moves into the previous column rather than
                // being deleted -- removing a column should not remove wording.
                const next = cols.map((c, i) => (i === ci - 1 ? [...c, ...cols[ci]] : c)).filter((_, i) => i !== ci);
                setCols(next);
                if (Array.isArray(selected.col_widths)) {
                  update(selected.id, { col_widths: selected.col_widths.filter((_, i) => i !== ci) });
                }
              };
              const addInto = (ci, type) => {
                const b = newBlock(type);
                setCols(cols.map((c, i) => (i === ci ? [...c, b] : c)));
                setSelectedId(b.id);
              };
              const moveChild = (ci, idx, delta) => {
                const col = cols[ci];
                const to = idx + delta;
                if (to < 0 || to >= col.length) return;
                const next = col.slice();
                [next[idx], next[to]] = [next[to], next[idx]];
                setCols(cols.map((c, i) => (i === ci ? next : c)));
              };
              const moveAcross = (ci, idx, delta) => {
                const to = ci + delta;
                if (to < 0 || to >= cols.length) return;
                const moving = cols[ci][idx];
                setCols(cols.map((c, i) => {
                  if (i === ci) return c.filter((_, j) => j !== idx);
                  if (i === to) return [...c, moving];
                  return c;
                }));
              };
              return (
                <>
                  <div style={{ fontSize: '11px', color: C?.muted, lineHeight: 1.6, marginBottom: '12px' }}>
                    Sections placed here sit beside each other. A row that will not fit moves to the
                    next page whole; one taller than a page is stacked instead, and the preview says so.
                  </div>
                  {cols.map((col, ci) => (
                    <div key={ci} style={{ marginBottom: '12px', border: `1px solid ${C?.border}`,
                                           borderRadius: '8px', padding: '8px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                    marginBottom: '6px' }}>
                        <span style={{ fontSize: '10px', fontWeight: 700, color: C?.muted,
                                       textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                          Column {ci + 1}
                        </span>
                        {cols.length > 2 && (
                          <button onClick={() => dropColumn(ci)} title="Remove this column; its sections move left"
                                  style={{ background: 'none', border: 'none', color: C?.muted,
                                           cursor: 'pointer', fontSize: '13px' }}>×</button>
                        )}
                      </div>
                      {col.map((child, idx) => (
                        <div key={child.id}
                             style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '3px' }}>
                          <button onClick={() => setSelectedId(child.id)}
                                  style={{ flex: 1, minWidth: 0, textAlign: 'left', fontSize: '11px',
                                           background: selectedId === child.id ? `${C?.accent}14` : 'transparent',
                                           border: `1px solid ${selectedId === child.id ? C?.accent : C?.border}`,
                                           borderRadius: '5px', color: C?.text, padding: '4px 6px', cursor: 'pointer',
                                           overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {BLOCK_TYPES[child.type]?.label || child.type}
                            {blockPlainText(child) ? ` — ${blockPlainText(child).slice(0, 18)}` : ''}
                          </button>
                          <button onClick={() => moveChild(ci, idx, -1)} disabled={idx === 0}
                                  title="Move up" style={miniBtn(C)}>↑</button>
                          <button onClick={() => moveChild(ci, idx, 1)} disabled={idx === col.length - 1}
                                  title="Move down" style={miniBtn(C)}>↓</button>
                          <button onClick={() => moveAcross(ci, idx, -1)} disabled={ci === 0}
                                  title="Move to the previous column" style={miniBtn(C)}>←</button>
                          <button onClick={() => moveAcross(ci, idx, 1)} disabled={ci === cols.length - 1}
                                  title="Move to the next column" style={miniBtn(C)}>→</button>
                        </div>
                      ))}
                      <select style={{ ...(css?.sel || {}), fontSize: '11px', marginTop: '4px' }}
                              value=""
                              onChange={(e) => { if (e.target.value) addInto(ci, e.target.value); }}>
                        <option value="">+ Add into this column…</option>
                        {COLUMN_CHILD_TYPES.map((t) => (
                          <option key={t} value={t}>{BLOCK_TYPES[t]?.label || t}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                  {cols.length < 3 && (
                    <button style={smallBtn(false)} onClick={addColumn}>+ Column</button>
                  )}

                  <div style={{ ...lbl, marginTop: '14px' }}>Column widths</div>
                  <div style={{ display: 'flex', gap: '4px', marginBottom: '4px' }}>
                    {tableColumnWidths(selected, cols.length).map((f, i) => (
                      <input key={i} type="number" min="1" step="1"
                             value={Array.isArray(selected.col_widths) ? selected.col_widths[i] : Math.round(f * 100)}
                             onChange={(e) => {
                               const base = Array.isArray(selected.col_widths)
                                 ? selected.col_widths.slice()
                                 : tableColumnWidths(selected, cols.length).map((w) => Math.round(w * 100));
                               base[i] = Number(e.target.value) || 1;
                               update(selected.id, { col_widths: base });
                             }}
                             style={{ ...(css?.inp || {}), flex: 1, minWidth: 0, padding: '4px 6px', fontSize: '11px' }} />
                    ))}
                  </div>
                  <div style={{ fontSize: '10px', color: C?.muted, lineHeight: 1.5, marginBottom: '12px' }}>
                    Relative, like a table’s — 2 and 1 makes the first column twice the second.
                  </div>

                  <div style={lbl}>Gap between columns</div>
                  <input type="number" min="0" max="72" value={selected.gap ?? 18}
                         onChange={(e) => update(selected.id, { gap: Number(e.target.value) || 0 })}
                         style={{ ...(css?.inp || {}), marginBottom: '12px' }} />
                </>
              );
            })()}

            {!pageSetup && !textStyles && selected?.type === 'divider' && (
              <ColorField
                label="Line color"
                hint="Leave it on Default and this line follows the Text styles panel."
                value={isHexColor(selected.color) ? selected.color : null}
                fallback={styles.divider?.color || 'rule'}
                onChange={(v) => update(selected.id, { color: v })}
                C={C} css={css} />
            )}

            {!pageSetup && !textStyles && selected && (BLOCK_TYPES[selected.type]?.text || BLOCK_TYPES[selected.type]?.items) && (
              <div style={{ marginBottom: '10px' }}>
                <div style={lbl}>
                  <TKTooltip text="Same every time: the wording is fixed. Filled when sending: you type this for each contract, and it becomes a question on the send form.">
                    How it’s filled ⓘ
                  </TKTooltip>
                </div>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {Object.entries(FILL_MODES).map(([mode, def]) => (
                    <button key={mode} style={smallBtn(selected.fill_mode === mode)}
                            onClick={() => update(selected.id, { fill_mode: mode })}>
                      {def.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {!pageSetup && !textStyles && selected?.fill_mode === 'prompted' && (
              <div style={{ padding: '10px', borderRadius: '7px', border: `1px solid ${C?.border}`,
                            background: 'rgba(127,142,162,0.06)', marginBottom: '10px' }}>
                <div style={lbl}>Label</div>
                <input style={css?.inp} value={selected.label || ''} placeholder="e.g. Scope of services"
                       onChange={(e) => update(selected.id, { label: e.target.value })} />
                <div style={{ ...lbl, marginTop: '8px' }}>Guidance</div>
                <textarea rows={2} value={selected.hint || ''} placeholder="What should go here?"
                          onChange={(e) => update(selected.id, { hint: e.target.value })}
                          style={{ ...(css?.inp || {}), resize: 'vertical', fontFamily: 'inherit' }} />
                <div style={{ ...lbl, marginTop: '8px' }}>Default text</div>
                <RichTextBox value={selected.default_text || ''} rows={3} css={css} C={C}
                             title="Default text"
                             target={tokens.target((v) => update(selected.id, { default_text: v }))}
                             onChange={(v) => update(selected.id, { default_text: v })} />
                <MergeFieldBank resolve={resolve} onInsert={tokens.insert} css={css} C={C}
                  note="Merge fields work in the default text, and in whatever the sender types." />
                <label style={{ display: 'flex', alignItems: 'center', gap: '7px', marginTop: '9px',
                                fontSize: '12px', color: C?.text, cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!selected.required}
                         onChange={(e) => update(selected.id, { required: e.target.checked })} />
                  Must be filled in before sending
                </label>
              </div>
            )}

            {!pageSetup && !textStyles && selected?.type === 'signature_block' && (
              <div style={{ marginBottom: '10px' }}>
                <div style={lbl}>Heading</div>
                <input style={css?.inp} value={selected.label || ''}
                       onChange={(e) => update(selected.id, { label: e.target.value })} />
                <div style={{ ...lbl, marginTop: '10px' }}>Columns</div>
                <div style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
                  {[[1, 'One'], [2, 'Two']].map(([n, label]) => (
                    <button key={n} style={smallBtn((Number(selected.columns) === 1 ? 1 : 2) === n)}
                            onClick={() => update(selected.id, { columns: n })}>{label}</button>
                  ))}
                </div>
                <div style={{ fontSize: '10px', color: C?.muted, marginBottom: '8px', lineHeight: 1.5 }}>
                  Each person gets their own column, in the order below. Two columns put the first two
                  side by side.
                </div>

                <div style={lbl}>
                  <TKTooltip text="The line above each column, e.g. For Acme Consulting. It belongs to the column position, so it stays put even if you change who signs there.">
                    Column headings ⓘ
                  </TKTooltip>
                </div>
                {Array.from({ length: Number(selected.columns) === 1 ? 1 : 2 }).map((_, i) => (
                  <input key={i} style={{ ...(css?.inp || {}), marginBottom: '5px' }}
                         placeholder={i === 0 ? 'e.g. For Acme Consulting' : 'e.g. For Acme Corp'}
                         value={(selected.column_headers || [])[i] || ''}
                         {...tokens.target((v) => {
                           const next = [...(selected.column_headers || ['', ''])];
                           next[i] = v;
                           update(selected.id, { column_headers: next });
                         })}
                         onChange={(e) => {
                           const next = [...(selected.column_headers || ['', ''])];
                           next[i] = e.target.value;
                           update(selected.id, { column_headers: next });
                         }} />
                ))}

                <div style={{ ...lbl, marginTop: '10px' }}>Who signs here</div>
                {templateMode ? (
                  <div style={{ fontSize: '11px', color: C?.muted, lineHeight: 1.55 }}>
                    Signature and date boxes belong to the people on a contract, so they’re placed
                    once you’ve made a contract from this template and entered who signs. The heading
                    and the layout of this block are saved here.
                  </div>
                ) : roles.length === 0 && (
                  <div style={{ fontSize: '11px', color: C?.badgeAmber }}>
                    Add signers under Edit details first.
                  </div>
                )}
                {!templateMode && orderedRoles.map((r, idx) => {
                  const who = r.person_name || r.name;
                  const has = (line) => lineExists(selected, r, line);
                  const twoUp = Number(selected.columns) !== 1;
                  // Where this person actually lands, said plainly -- the
                  // heading above a column and the person under it are set in
                  // two different places, and they can disagree.
                  // The heading as the READER will see it, tokens resolved --
                  // the whole point of this line is to catch a person sitting
                  // under the wrong heading.
                  const headingHere = resolveLines((selected.column_headers || [])[idx % 2] || '', resolve)
                    .map((line) => line.map((r) => r.text).join('')).join(' ').trim();
                  const place = twoUp
                    ? `Column ${(idx % 2) + 1}${headingHere ? ` \u00b7 ${headingHere}` : ''}`
                    : `${idx + 1}${idx === 0 ? 'st' : idx === 1 ? 'nd' : idx === 2 ? 'rd' : 'th'}`;
                  return (
                    <div key={r.id} style={{ padding: '8px', borderRadius: '6px', marginBottom: '6px',
                                             border: `1px solid ${C?.border}` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                    gap: '8px', marginBottom: '2px' }}>
                        <span style={{ fontSize: '12px', color: C?.textBright }}>{who}</span>
                        <span style={{ display: 'flex', gap: '4px' }}>
                          <button style={{ ...smallBtn(false), opacity: idx === 0 ? 0.35 : 1 }}
                                  disabled={idx === 0}
                                  title={twoUp ? 'Move left' : 'Move up'}
                                  onClick={() => moveSigner(selected, r.id, -1)}>
                            {twoUp ? '\u2190' : '\u2191'}
                          </button>
                          <button style={{ ...smallBtn(false), opacity: idx === orderedRoles.length - 1 ? 0.35 : 1 }}
                                  disabled={idx === orderedRoles.length - 1}
                                  title={twoUp ? 'Move right' : 'Move down'}
                                  onClick={() => moveSigner(selected, r.id, 1)}>
                            {twoUp ? '\u2192' : '\u2193'}
                          </button>
                          <button style={smallBtn(false)} onClick={() => addStandardLines(selected, r)}>
                            Standard set
                          </button>
                        </span>
                      </div>
                      <div style={{ fontSize: '10px', color: C?.muted, marginBottom: '6px' }}>{place}</div>
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {SIGNATURE_LINE_ORDER.map((line) => (
                          <button key={line} style={smallBtn(has(line))}
                                  title={SIGNATURE_LINES[line].hint}
                                  onClick={() => toggleLine(selected, r, line)}>
                            {SIGNATURE_LINES[line].label}
                          </button>
                        ))}
                      </div>
                      {/* Who supplies the value, per line.
                          A job title is the case this exists for: TimeKeep
                          knows the name and email it sent the document to, and
                          almost never knows the title — so a merge field for it
                          prints a blank line nobody was ever asked to fill,
                          which is exactly what happened on the first real
                          signing. Asking is the default for a title and not for
                          the other two, and either way it is the sender's call,
                          made before the document goes out. */}
                      {SIGNATURE_LINE_ORDER
                        .filter((line) => SIGNATURE_LINES[line].askable && has(line))
                        .map((line) => {
                          const def = SIGNATURE_LINES[line];
                          const f = fieldForLine(selected, r, line);
                          const asked = f.field_kind === 'text';
                          return (
                            <div key={`${line}-fill`}
                                 style={{ display: 'flex', alignItems: 'center', gap: '6px',
                                          flexWrap: 'wrap', marginTop: '6px' }}>
                              <span style={{ fontSize: '11px', color: C?.muted, minWidth: '58px' }}>
                                {def.label}
                              </span>
                              <button style={smallBtn(!asked)}
                                      title={`TimeKeep prints this from ${who}'s record on the signed copy.`}
                                      onClick={() => setLineAsked(line, f, false, r)}>
                                TimeKeep fills it
                              </button>
                              <button style={smallBtn(asked)}
                                      title="An empty box they type into as they sign."
                                      onClick={() => setLineAsked(line, f, true, r)}>
                                They type it
                              </button>
                              {asked && (
                                <label style={{ display: 'flex', alignItems: 'center', gap: '4px',
                                                fontSize: '11px', color: C?.text, cursor: 'pointer' }}>
                                  <input type="checkbox" checked={!!f.is_required}
                                         onChange={(e) => setLineRequired(f, e.target.checked)} />
                                  Required
                                </label>
                              )}
                            </div>
                          );
                        })}
                      <div style={{ fontSize: '10px', color: C?.muted, marginTop: '5px', lineHeight: 1.5 }}>
                        Name, title and email can either be filled in from this signer or typed by them
                        as they sign — a required one has to be answered before they can sign. Signature
                        and initials are always drawn by them.
                      </div>
                    </div>
                  );
                })}

                {/* Option 2: a blank that gets stamped with the real effective
                    date once the last signature lands. The body text can only
                    describe that date; this prints it. Like the signature
                    boxes, it is a contract_fields row, so not placeable here. */}
                {/* Only worth offering when the date is genuinely unknown
                    until signing. With a date already set, the document can
                    simply say it, and an empty line would be a puzzle. */}
                {!templateMode && contract.effective_on_signature && (<>
                <div style={{ ...lbl, marginTop: '12px' }}>Effective date line</div>
                <button style={smallBtn(fields.some((f) => f.block_id === selected.id && f.merge_token === '{{effective_date}}'))}
                        onClick={() => toggleMergeField(selected, '{{effective_date}}', 'Effective date')}>
                  {fields.some((f) => f.block_id === selected.id && f.merge_token === '{{effective_date}}')
                    ? 'Remove the effective date line' : 'Add an effective date line'}
                </button>
                <div style={{ fontSize: '10px', color: C?.muted, marginTop: '5px', lineHeight: 1.55 }}>
                  This contract takes effect on the day it’s signed, so that date isn’t known yet. This
                  adds an “Effective date” line below the columns — blank in the document you send, filled
                  in on the signed copy with the date it actually took effect. It belongs to no one signer,
                  so it sits under both columns rather than in either.
                </div>
                </>)}
              </div>
            )}

            {!pageSetup && !textStyles && selected?.type === 'spacer' && (
              <div style={{ marginBottom: '10px' }}>
                <div style={lbl}>Height</div>
                <input type="number" min={4} max={200} style={css?.inp} value={selected.height || 18}
                       onChange={(e) => update(selected.id, { height: Number(e.target.value) })} />
              </div>
            )}

            {!pageSetup && !textStyles && selected?.type === 'options' && !templateMode && (
              <div style={{ marginBottom: '14px' }}>
                <div style={lbl}>The choices</div>
                <div style={{ fontSize: '11px', color: C?.muted, lineHeight: 1.6, marginBottom: '8px' }}>
                  {optionGroups.length === 0
                    ? 'Nothing on offer yet, so this section prints nothing.'
                    : `${optionRows.length} option${optionRows.length === 1 ? '' : 's'} in ` +
                      `${optionGroups.length} choice${optionGroups.length === 1 ? '' : 's'}. ` +
                      'Each prints here with its price and a box beside it; the box next to ' +
                      'whichever one they pick is ticked on the signed copy.'}
                </div>
                <button style={css?.btn?.('primary')} disabled={busy}
                        onClick={() => openOptions(optionGroups.length === 0)}>
                  {optionGroups.length === 0 ? 'Set up the choices' : 'Edit pricing options'}
                </button>
              </div>
            )}

            {!pageSetup && !textStyles && selected && (
              <button style={css?.smBtn?.(C?.danger)} onClick={() => removeBlock(selected.id)}>
                Delete section
              </button>
            )}
          </div>

          {optionsOpen && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 9800,
                          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}
                 onClick={() => { setOptionsOpen(false); loadOptions(); }}>
              <div onClick={(e) => e.stopPropagation()}
                   style={{ background: C?.surface, border: `1px solid ${C?.border}`, borderRadius: '12px',
                            width: '720px', maxWidth: '100%', maxHeight: '86vh', overflowY: 'auto',
                            padding: '22px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                  <div>
                    <div style={{ fontSize: '16px', fontWeight: 700, color: C?.textBright }}>
                      Pricing options
                    </div>
                    <div style={{ fontSize: '11px', color: C?.muted, marginTop: '3px', lineHeight: 1.6 }}>
                      These print in the document wherever the Pricing options section sits — each
                      one with its price and a box beside it. The option they choose becomes this
                      client’s billing terms, so the price here is the price they are billed.
                    </div>
                  </div>
                  <button style={css?.btn?.('primary')}
                          onClick={() => { setOptionsOpen(false); loadOptions(); }}>Done</button>
                </div>
                <ContractOptions contract={contract} css={css} C={C} hideHeading
                                 onError={onError} onChanged={loadOptions} />
              </div>
            </div>
          )}

          {(validation.errors.length > 0 || validation.warnings.length > 0) && (
            <div style={{ borderTop: `1px solid ${C?.border}`, padding: '10px 12px', maxHeight: '160px', overflowY: 'auto' }}>
              {validation.errors.map((e, i) => (
                <div key={`e${i}`} style={{ fontSize: '11px', color: C?.danger, marginBottom: '4px', lineHeight: 1.45 }}>
                  {e.message}
                </div>
              ))}
              {validation.warnings.map((w, i) => (
                <div key={`w${i}`} style={{ fontSize: '11px', color: C?.badgeAmber, marginBottom: '4px', lineHeight: 1.45 }}>
                  {w.message}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
